// pages/MarketAdmin.jsx
// DWI Market — the whole marketplace run from Admin → Market, no code edits:
// orders, products (photos, prices per size, stock), sizes, categories,
// delivery areas, and settings (seller, fee, alert phone, shop on/off).
// Phase 1: any category, price on request, PRIVATE supplier details + cost,
// auto prices (cost + margin %), driver details on dispatch. Supplier, cost,
// margin and trip cost only ever appear here — never in the buyer shop.
// Phase 2: Sellers (vet who sells to DWI) and Supply (every seller listing —
// your private supply catalog) with "Publish to shop". Buyers never see these.
// Phase 3: Demand (buyers' buy requests) next to matching supply + products,
// and quotes the buyer accepts into a normal order with a pay link.
// Phase 5: you can start a conversation from any seller, listing, buy request
// or order ("💬 Message"); open chats refresh every few seconds; photos.
// Phase 6: Dashboard (first tab), Payables (what DWI owes sellers — records
// only, no money moves) and Margin report.
import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import toast from 'react-hot-toast';
import { dwisendShopAPI, marketAPI } from '../services/api';
import { Button, Card, Input, Select, Badge, Modal, Table, Td, Empty, Spinner, SectionHeader } from '../components/ui';
import { Conversation, MessageBox } from './Market';
import { SupplyPhoto, LISTING_STATUS } from './MarketSell';
import { REQUEST_STATUS, QUOTE_STATUS } from './MarketRequests';
import { useAutoRefresh, mergeMessages, AttachPhoto } from './MarketChat';
import { DashboardSection, PayablesSection, MarginSection } from './MarketMoney';
import { CitiesSection } from './DwiSendCities';
import { LogisticsSection } from './DwiSendLogistics';
import { ApprovalsPanel } from './DwiSendApprovals';
import { DrcRequestsSection } from './DwiSendDrcAdmin';
import { DrcShopSection, DrcListingButton } from './DwiSendShopAdmin';
import { AdminEditListingModal, ListingEditHistory } from './MarketListingEdits';
import { sizeWarnings } from './sizeHints';
import { useLocation } from 'react-router-dom';
import { useOrderAlerts } from '../context/OrderAlertsContext';
import { pinNew } from '../hooks/orderAlerts';

const ugx = n => `UGX ${Number(n || 0).toLocaleString('en-US')}`;
const when = d => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

const STATUS_COLOR = {
  awaiting_payment: 'amber', paid: 'cyan', dispatched: 'blue', delivered: 'teal',
  completed: 'green', cancelled: 'gray', expired: 'gray',
};
const STATUS_LABEL = {
  awaiting_payment: 'Awaiting payment', paid: 'Paid', dispatched: 'Dispatched', delivered: 'Delivered',
  completed: 'Completed', cancelled: 'Cancelled', expired: 'Expired',
};

const SECTIONS = [
  ['dashboard', '📊 Dashboard'],
  ['orders', '🧾 Orders'],
  ['messages', '💬 Messages'],
  ['demand', '📝 Demand'],
  ['drc', '🇨🇩 DRC requests'],
  ['drcshop', '🇨🇩 DRC shop'],
  ['supply', '🌾 Supply'],
  ['sellers', '🧑‍🌾 Sellers'],
  ['payables', '💸 Payables'],
  ['report', '📈 Margin'],
  ['products', '📦 Products & Stock'],
  ['unit', '⚖ Sizes'],
  ['category', '🗂 Categories'],
  ['zone', '🚚 Delivery Areas'],
  ['cities', '🏙️ Cities'],
  ['logistics', '🚛 Logistics'],
  ['approvals', '✅ Approvals'],
  ['settings', '⚙ Settings'],
];

// "💬 Message" anywhere in Admin → Market opens the start-a-conversation box.
// target: { userId, name, topic, orderId?, listingId?, requestId?, subject?, about? }
const ChatCtx = createContext(() => {});
const useStartChat = () => useContext(ChatCtx);

function MessageButton({ target, label = '💬 Message' }) {
  const startChat = useStartChat();
  if (!target?.userId) return null;
  return <Button size="xs" variant="secondary" onClick={e => { e.stopPropagation(); startChat(target); }}>{label}</Button>;
}

export function MarketAdminTab() {
  const [section, setSection] = useState(() => new URLSearchParams(window.location.search).get('section') || 'dashboard');
  // Follow ?section= when the address changes (e.g. "Open" on a new-order banner).
  const { search: locSearch } = useLocation();
  useEffect(() => { const s = new URLSearchParams(locSearch).get('section'); if (s) setSection(s); }, [locSearch]);
  const { countUG, countDRC } = useOrderAlerts();
  const newCount = { orders: countUG, drcshop: countDRC };
  const [supplySeller, setSupplySeller] = useState(null);   // Sellers → "Listings" opens Supply filtered to one seller
  const [chatTarget, setChatTarget] = useState(null);
  const [openThread, setOpenThread] = useState(null);       // jump straight into a conversation
  const [unread, setUnread] = useState(0);
  const loadUnread = useCallback(() => marketAPI.unread().then(r => setUnread(r.dwiUnread || 0)).catch(() => {}), []);
  useEffect(() => { loadUnread(); }, [loadUnread, section]);
  useAutoRefresh(loadUnread, 20);
  const goToThread = id => { setOpenThread(id); setSection('messages'); };

  return (
    <ChatCtx.Provider value={setChatTarget}>
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {SECTIONS.map(([id, label]) => (
          <button key={id} onClick={() => { setSection(id); if (id === 'messages') setOpenThread(null); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${section === id
              ? 'bg-cyan text-bg' : 'bg-bg-panel text-text-secondary border border-border hover:border-border-hi'}`}>
            {label}{id === 'messages' && unread ? ` (${unread})` : ''}
            {newCount[id] > 0 && <span className="ml-1.5 px-1.5 rounded-full bg-red text-white text-[10px] font-black animate-pulse" data-testid={`section-new-${id}`}>{newCount[id]} NEW</span>}
          </button>
        ))}
      </div>
      {section === 'dashboard' && <DashboardSection goTo={setSection} />}
      {section === 'orders'   && <OrdersSection />}
      {section === 'messages' && <MessagesSection openId={openThread} setOpenId={setOpenThread} />}
      {section === 'demand'   && <DemandSection />}
      {section === 'drc'      && <DrcRequestsSection />}
      {section === 'drcshop'  && <DrcShopSection />}
      {section === 'supply'   && <SupplySection seller={supplySeller} onClearSeller={() => setSupplySeller(null)} />}
      {section === 'sellers'  && <SellersSection onListings={s => { setSupplySeller(s); setSection('supply'); }} />}
      {section === 'products' && <ProductsSection />}
      {section === 'unit'     && <CatalogSection kind="unit" />}
      {section === 'category' && <CatalogSection kind="category" />}
      {section === 'zone'     && <CatalogSection kind="zone" />}
      {section === 'cities'   && <CitiesSection />}
      {section === 'logistics' && <LogisticsSection />}
      {section === 'approvals' && <ApprovalsPanel />}
      {section === 'payables' && <PayablesSection />}
      {section === 'report'   && <MarginSection />}
      {section === 'settings' && <SettingsSection />}
      {chatTarget && <StartChatModal target={chatTarget} onClose={() => setChatTarget(null)}
        onSent={t => { setChatTarget(null); goToThread(t._id); }} />}
    </div>
    </ChatCtx.Provider>
  );
}

// ── Marketing page counts (anonymous, per traffic source) ────────────────────
const LANDING_COLS = [
  ['visit', 'Visits'], ['click_buy', 'Buy'], ['click_sell', 'Sell'],
  ['click_whatsapp', 'WhatsApp'], ['click_call', 'Call'], ['click_login', 'Log in'],
  ['product_view', 'Product views'], ['click_order', 'Order'], ['click_share', 'Shares'],
];

function LandingStats() {
  const [days, setDays]   = useState(30);
  const [stats, setStats] = useState(null);
  useEffect(() => {
    marketAPI.landingStats(days).then(r => setStats(r.stats)).catch(err => toast.error(err.message));
  }, [days]);

  if (!stats) return <div className="py-6 flex justify-center"><Spinner /></div>;
  const rows = [{ src: 'All sources', ...stats.totals }, ...stats.sources];
  return (
    <div className="mt-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-xs font-bold text-text-muted uppercase tracking-wider">Visits & button clicks</div>
        <Select value={days} onChange={e => setDays(Number(e.target.value))}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </Select>
      </div>
      {stats.totals.visit === 0 && stats.sources.length === 0
        ? <p className="text-xs text-text-muted">No visits yet{stats.days ? ` in the last ${stats.days} days` : ''}.</p>
        : (
          <Table headers={['Source', ...LANDING_COLS.map(([, l]) => l)]}>
            {rows.map(r => (
              <tr key={r.src}>
                <Td className={r.src === 'All sources' ? 'font-bold text-text-primary' : ''}>{r.src}</Td>
                {LANDING_COLS.map(([k]) => <Td key={k}>{r[k] || 0}</Td>)}
              </tr>
            ))}
          </Table>
        )}
      <p className="text-[11px] text-text-muted mt-2">Visits are counted once per browser session. "direct" = no source on the link. Sign-ups themselves are not linked to a source.</p>
    </div>
  );
}

// ── Messages inbox — every conversation is one user ↔ DWI (you reply as "DWI Market")
const TOPIC_TEXT = { order: 'Order', selling: 'Selling to DWI', buying: 'Buy request', general: 'Question' };
const TOPIC_COLOR = { order: 'cyan', selling: 'green', buying: 'amber', general: 'gray' };

function MessagesSection({ openId, setOpenId }) {
  const [filter, setFilter] = useState({ status: 'open', topic: '' });
  const [data, setData]     = useState(null);
  const startChat = useStartChat();

  const load = useCallback(async () => {
    try { setData(await marketAPI.adminThreads({ status: filter.status || undefined, topic: filter.topic || undefined })); }
    catch (err) { toast.error(err.message); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load, 15, !openId);   // new conversations appear without reloading

  if (openId) return <AdminThread id={openId} onBack={() => { setOpenId(null); load(); }} />;

  return (
    <Card>
      <SectionHeader title={`Messages${data?.unread ? ` — ${data.unread} unread` : ''}`} action={
        <div className="flex gap-2">
          <Select value={filter.topic} onChange={e => setFilter(f => ({ ...f, topic: e.target.value }))}>
            <option value="">All topics</option>
            <option value="order">Orders</option>
            <option value="buying">Buy requests</option>
            <option value="selling">Selling to DWI</option>
            <option value="general">Questions</option>
          </Select>
          <Select value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="">All</option>
          </Select>
          <Button size="sm" onClick={() => startChat({ topic: 'general' })}>+ New conversation</Button>
        </div>
      } />
      <p className="text-[11px] text-text-muted mb-3">
        Buyers and sellers only ever talk to DWI — they never see each other or which admin replied. Open chats refresh every few
        seconds (set in Settings). Start a conversation here or with "💬 Message" on any seller, listing, buy request or order.
      </p>
      {!data ? <div className="py-10 flex justify-center"><Spinner /></div>
        : data.threads.length === 0 ? <Empty icon="💬" title="No conversations" />
        : (
          <Table headers={['', 'With', 'About', 'Last message', 'When', '']}>
            {data.threads.map(t => (
              <tr key={t._id} className="cursor-pointer" onClick={() => setOpenId(t._id)}>
                <Td>{t.unreadForDwi && <span className="text-cyan">●</span>}</Td>
                <Td><div className="font-semibold">{t.userId?.name || '—'}</div><div className="text-[11px] text-text-muted">{t.userId?.phone || t.userId?.email}</div></Td>
                <Td>
                  <Badge label={TOPIC_TEXT[t.topic]} color={TOPIC_COLOR[t.topic]} />
                  {(t.orderRef || t.listingRef || t.requestRef) && <div className="font-mono text-[11px] text-cyan mt-1">{t.orderRef || t.listingRef || t.requestRef}</div>}
                  {t.startedBy === 'dwi' && <div className="text-[10px] text-text-muted">started by DWI</div>}
                </Td>
                <Td><div className="font-semibold text-xs">{t.subject}</div><div className="text-[11px] text-text-muted truncate max-w-[260px]">{t.lastMessage}</div></Td>
                <Td className="text-[11px] text-text-muted">{when(t.lastMessageAt)}</Td>
                <Td><Button size="sm" variant="secondary">Open</Button></Td>
              </tr>
            ))}
          </Table>
        )}
    </Card>
  );
}

function AdminThread({ id, onBack }) {
  const [t, setT]       = useState(null);
  const [body, setBody] = useState('');
  const [photo, setPhoto] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { marketAPI.adminThread(id).then(r => setT(r.thread)).catch(err => toast.error(err.message)); }, [id]);
  // Check for the user's new messages every few seconds while open.
  useAutoRefresh(async () => {
    if (!t) return;
    const last = t.messages[t.messages.length - 1]?.createdAt;
    const r = await marketAPI.adminThread(id, last);
    setT(cur => (cur ? { ...cur, ...r.thread, messages: mergeMessages(cur.messages, r.thread.messages) } : r.thread));
  });

  const run = async (fn, ok) => {
    setBusy(true);
    try { setT((await fn()).thread); if (ok) toast.success(ok); return true; }
    catch (err) { toast.error(err.message); return false; }
    finally { setBusy(false); }
  };

  if (!t) return <Card><div className="py-10 flex justify-center"><Spinner /></div></Card>;
  const u = t.userId || {};
  const c = t.contact || {};
  const ref = t.orderRef || t.listingRef || t.requestRef;
  return (
    <Card>
      <div className="flex items-start justify-between gap-2 flex-wrap mb-3">
        <div>
          <div className="text-text-primary text-sm font-bold">{t.subject}</div>
          <div className="text-[11px] text-text-muted">
            {TOPIC_TEXT[t.topic]}{ref ? ` · ${ref}` : ''} · {c.name || u.name || '—'}{u.email ? ` · ${u.email}` : ''} · refreshes every few seconds
          </div>
          <ContactButtons phone={c.phone || u.phone} whatsapp={c.whatsapp} text={`Hello ${c.name || u.name || ''}, this is DwiSend${ref ? ` about ${ref}` : ''}. `} />
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" loading={busy}
            onClick={() => run(() => marketAPI.adminThreadStatus(id, t.status === 'open' ? 'closed' : 'open'), t.status === 'open' ? 'Closed' : 'Reopened')}>
            {t.status === 'open' ? 'Close' : 'Reopen'}
          </Button>
          <Button size="sm" variant="ghost" onClick={onBack}>← Inbox</Button>
        </div>
      </div>
      <Conversation messages={t.messages} mine="dwi" nameOf={() => c.name || u.name || 'User'} />
      <MessageBox value={body} onChange={setBody} placeholder='Reply as "DwiSend"…' />
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <Button size="sm" loading={busy} disabled={!body.trim() && !photo}
          onClick={() => run(() => marketAPI.adminReply(id, body, photo || undefined), 'Reply sent').then(sent => { if (sent) { setBody(''); setPhoto(''); } })}>Send reply</Button>
        <AttachPhoto admin photo={photo} onChange={setPhoto} />
      </div>
    </Card>
  );
}

// Start (or continue) a conversation as "DWI Market". With no user given, search for one.
function StartChatModal({ target, onClose, onSent }) {
  const [user, setUser]   = useState(target.userId ? { _id: target.userId, name: target.name } : null);
  const [q, setQ]         = useState('');
  const [found, setFound] = useState([]);
  const [subject, setSubject] = useState(target.subject || '');
  const [body, setBody]   = useState('');
  const [photo, setPhoto] = useState('');
  const [busy, setBusy]   = useState(false);

  useEffect(() => {
    if (user || q.trim().length < 2) { setFound([]); return undefined; }
    const h = setTimeout(() => marketAPI.adminUsers(q.trim()).then(r => setFound(r.users || [])).catch(err => toast.error(err.message)), 300);
    return () => clearTimeout(h);
  }, [q, user]);

  const send = async () => {
    setBusy(true);
    try {
      const r = await marketAPI.adminStartThread({ userId: user._id, topic: target.topic || 'general', orderId: target.orderId,
        listingId: target.listingId, requestId: target.requestId, subject, body, photo: photo || undefined });
      toast.success(`Sent to ${user.name || 'the user'} — they see it from "DwiSend"`);
      onSent(r.thread);
    } catch (err) { toast.error(err.message); setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Message ${user?.name || 'a buyer or seller'}${target.about ? ` — ${target.about}` : ''}`} size="sm">
      <div className="space-y-3">
        {!user ? (
          <div>
            <Input label="Find the person (name, phone or email)" value={q} onChange={e => setQ(e.target.value)} autoFocus />
            <div className="mt-2 space-y-1">
              {found.map(f => (
                <button key={f._id} className="w-full text-left text-xs px-3 py-2 rounded-lg border border-border hover:border-cyan" onClick={() => setUser(f)}>
                  <span className="text-text-primary font-semibold">{f.name || '—'}</span> · {f.phone || ''} {f.email || ''}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {target.about && <p className="text-xs text-text-muted">About {target.about}. If there's already an open conversation about it, this message is added to it.</p>}
            {!target.orderId && !target.listingId && !target.requestId && (
              <Input label="Subject (optional)" value={subject} maxLength={120} onChange={e => setSubject(e.target.value)} />
            )}
            <MessageBox value={body} onChange={setBody} placeholder='Write as "DwiSend"…' />
            <AttachPhoto admin photo={photo} onChange={setPhoto} />
          </>
        )}
        <div className="flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1" loading={busy} disabled={!user || (!body.trim() && !photo)} onClick={send}>Send</Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Demand (Phase 3): buyers' buy requests → quotes → orders ─────────────────
const REQ_COLOR = { new: 'amber', sourcing: 'cyan', quoted: 'green', won: 'teal', lost: 'gray', cancelled: 'gray' };
const ADMIN_REQ_LABEL = { new: 'New', sourcing: 'Sourcing', quoted: 'Quoted', won: 'Won', lost: 'Lost', cancelled: 'Cancelled by buyer' };
const fmtDay = d => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '');

function DemandSection() {
  const [filter, setFilter] = useState({ status: 'open', categoryId: '', q: '' });
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [openId, setOpenId] = useState(null);

  useEffect(() => { marketAPI.listCatalog('category').then(r => setCategories(r.items || [])).catch(err => toast.error(err.message)); }, []);
  const load = useCallback(async () => {
    try { setData(await marketAPI.adminRequests({ status: filter.status || undefined, categoryId: filter.categoryId || undefined, q: filter.q.trim() || undefined })); }
    catch (err) { toast.error(err.message); }
  }, [filter]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const setF = (k, v) => setFilter(f => ({ ...f, [k]: v }));
  const c = data?.counts || {};

  return (
    <Card>
      <SectionHeader title={`Demand — what buyers want${c.new ? ` (${c.new} new)` : ''}`} action={
        <div className="flex gap-2 items-center flex-wrap">
          <Input value={filter.q} onChange={e => setF('q', e.target.value)} placeholder="Search product, REQ-ref, phone, place" />
          <Select value={filter.categoryId} onChange={e => setF('categoryId', e.target.value)}>
            <option value="">All categories</option>
            {categories.map(cat => <option key={cat._id} value={cat._id}>{cat.name}</option>)}
          </Select>
          <Select value={filter.status} onChange={e => setF('status', e.target.value)}>
            <option value="open">Open (new, sourcing, quoted)</option>
            {Object.entries(ADMIN_REQ_LABEL).map(([k, v]) => <option key={k} value={k}>{v} ({c[k] || 0})</option>)}
            <option value="">All</option>
          </Select>
        </div>
      } />
      <p className="text-[11px] text-text-muted mb-3">
        Buy requests from buyers. Open one to see matching seller supply and shop products, then send a quote. When the buyer accepts,
        it becomes a normal order (Orders tab) with a pay link. Buyers only ever deal with DWI. Buy requests have their own switch in Settings.
      </p>
      {!data ? <div className="py-10 flex justify-center"><Spinner /></div>
        : data.requests.length === 0 ? <Empty icon="📝" title="No buy requests" description="When buy requests are ON, they appear here and you get an alert." />
        : (
          <Table headers={['Request', 'Wants', 'Buyer', 'Deliver to', 'Status', '']}>
            {data.requests.map(r => (
              <tr key={r._id} className="align-top cursor-pointer" onClick={() => setOpenId(r._id)}>
                <Td>
                  <div className="text-text-primary font-semibold">{r.productName}</div>
                  <div className="text-[11px] text-text-muted">{r.ref} · {r.categoryName} · {when(r.createdAt)}</div>
                  {r.adminNotes && <div className="text-[11px] text-amber mt-1 max-w-[200px] truncate">🔒 {r.adminNotes}</div>}
                </Td>
                <Td>
                  <div className="text-xs">{r.quantity.toLocaleString()} × {r.unitName}{r.baseQty > 1 ? ` (${(r.quantity * r.baseQty).toLocaleString()} ${r.baseUnit})` : ''}</div>
                  {r.targetPriceUGX && <div className="text-[11px] text-text-muted">target {ugx(r.targetPriceUGX)} each</div>}
                  {r.neededBy && <div className="text-[11px] text-text-muted">by {fmtDay(r.neededBy)}</div>}
                </Td>
                <Td onClick={e => e.stopPropagation()}>
                  <div className="text-xs text-text-primary">{r.buyer?.name || '—'}</div>
                  <ContactButtons phone={r.phone} whatsapp={r.whatsapp} text={`Hello ${r.buyer?.name || ''}, this is DwiSend about your request ${r.ref} (${r.productName}). `} />
                </Td>
                <Td><div className="text-xs">{r.deliveryZoneName || '—'}</div><div className="text-[11px] text-text-muted max-w-[160px] truncate">{r.deliveryAddress}</div></Td>
                <Td>
                  <Badge label={ADMIN_REQ_LABEL[r.status]} color={REQ_COLOR[r.status]} />
                  {r.latestQuote && <div className="text-[11px] text-text-muted mt-1">{r.latestQuote.ref}: {ugx(r.latestQuote.totalUGX)} · {QUOTE_STATUS[r.latestQuote.status]?.[0]}</div>}
                  {r.latestQuote?.order && <div className="text-[11px] text-cyan font-mono">{r.latestQuote.order.orderRef} · {STATUS_LABEL[r.latestQuote.order.status]}</div>}
                </Td>
                <Td><Button size="xs" variant="secondary">Open</Button></Td>
              </tr>
            ))}
          </Table>
        )}
      {openId && <RequestDetail id={openId} onClose={() => { setOpenId(null); load(); }} />}
    </Card>
  );
}

function RequestDetail({ id, onClose }) {
  const [r, setR] = useState(null);
  const [settings, setSettings] = useState(null);
  const [lines, setLines] = useState([]);   // quote being built
  const [prompt, setPrompt] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [lastSent, setLastSent] = useState(null);

  const load = useCallback(async () => {
    try { setR((await marketAPI.adminRequest(id)).request); } catch (err) { toast.error(err.message); }
  }, [id]);
  useEffect(() => { load(); marketAPI.getSettings().then(x => setSettings(x.settings)).catch(() => {}); }, [load]);

  if (!r) return <Modal open onClose={onClose} title="Buy request" size="lg"><div className="py-10 flex justify-center"><Spinner /></div></Modal>;
  const open = ['new', 'sourcing', 'quoted'].includes(r.status);
  const round = settings?.priceRoundUGX || 1;
  const suggest = cost => (settings?.defaultMarginPct != null ? autoPrice(cost, settings.defaultMarginPct, round) : cost);
  const sameUnit = unitName => unitName === r.unitName;

  const addProduct = (p, v) => setLines(ls => [...ls, { kind: 'product', productId: p._id, variantId: v._id, name: p.name, unitName: v.unitName,
    qty: sameUnit(v.unitName) ? r.quantity : 1, unitPriceUGX: v.priceUGX ?? '', costUGX: v.costUGX, inStock: v.inStock }]);
  const addSupply = s => setLines(ls => [...ls, { kind: 'supply', supplyListingId: s._id, name: s.productName, unitName: s.unitName,
    qty: Math.min(sameUnit(s.unitName) ? r.quantity : 1, s.quantity) || 1, unitPriceUGX: suggest(s.priceUGX), costUGX: s.priceUGX, ref: s.ref }]);
  const addFree = () => setLines(ls => [...ls, { kind: 'free', name: r.productName, unitName: r.unitName, qty: r.quantity, unitPriceUGX: '', costUGX: '' }]);

  const setStatus = async (status, reason) => {
    try { await marketAPI.requestStatus(r._id, status, reason); toast.success('Status updated'); load(); }
    catch (err) { toast.error(err.message); throw err; }
  };
  const withdraw = async q => {
    try { await marketAPI.withdrawQuote(q._id); toast.success('Quote withdrawn'); load(); } catch (err) { toast.error(err.message); }
  };
  const buyerWa = (text) => r.whatsapp && waLink(r.whatsapp, text);

  return (
    <Modal open onClose={onClose} title={`${r.productName} — ${r.ref}`} size="lg">
      <div className="space-y-5 text-sm">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Buyer" value={`${r.buyer?.name || '—'}${r.buyer?.email ? ` · ${r.buyer.email}` : ''}`} />
          <Field label="Status" value={<Badge label={ADMIN_REQ_LABEL[r.status]} color={REQ_COLOR[r.status]} />} />
          <Field label="Wants" value={`${r.quantity.toLocaleString()} × ${r.unitName}${r.baseQty > 1 ? ` (${(r.quantity * r.baseQty).toLocaleString()} ${r.baseUnit})` : ''} · ${r.categoryName}`} />
          <Field label="Target price" value={r.targetPriceUGX ? `${ugx(r.targetPriceUGX)} per ${r.unitName} · all: ${ugx(r.targetPriceUGX * r.quantity)}` : '—'} />
          <Field label="Needed by" value={r.neededBy ? new Date(r.neededBy).toLocaleDateString('en-GB') : 'Not given'} />
          <Field label="Deliver to" value={`${r.deliveryZoneName ? `${r.deliveryZoneName} (${ugx(r.deliveryZoneFeeUGX)}) · ` : ''}${r.deliveryAddress}`} />
          {r.notes && <Field label="Buyer's notes" value={r.notes} />}
          {r.statusReason && <Field label="Reason" value={r.statusReason} />}
        </div>
        <ContactButtons phone={r.phone} whatsapp={r.whatsapp} text={`Hello ${r.buyer?.name || ''}, this is DwiSend about your request ${r.ref} (${r.productName}). `} />
        <div className="flex gap-2 flex-wrap">
          {['new', 'quoted', 'lost'].includes(r.status) && <Button size="xs" variant="secondary" onClick={() => setStatus('sourcing').catch(() => {})}>{r.status === 'lost' ? 'Reopen (sourcing)' : 'Mark sourcing'}</Button>}
          {open && <Button size="xs" variant="ghost" onClick={() => setPrompt({ title: `Close ${r.ref} as lost`, required: true, confirmLabel: 'Close as lost',
            hint: 'The buyer sees this reason. Any quote waiting for them is withdrawn.', run: note => setStatus('lost', note) })}>Close as lost</Button>}
          <Button size="xs" variant="secondary" onClick={() => setNotesOpen(true)}>🔒 Notes{r.adminNotes ? ' ✓' : ''}</Button>
          <MessageButton target={{ userId: r.buyerId, name: r.buyer?.name, topic: 'buying', requestId: r._id, about: `request ${r.ref} (${r.productName})` }} label="💬 Message the buyer" />
        </div>
        {r.adminNotes && <p className="text-xs text-amber">🔒 {r.adminNotes}</p>}

        {r.quotes.length > 0 && (
          <div>
            <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider mb-2">Quotes</div>
            {r.quotes.map(q => (
              <div key={q._id} className="border border-border rounded-xl p-3 mb-2">
                <div className="flex justify-between gap-2 flex-wrap">
                  <div className="font-semibold text-text-primary">{q.ref} · {ugx(q.totalUGX)}</div>
                  <Badge label={QUOTE_STATUS[q.status]?.[0] || q.status} color={QUOTE_STATUS[q.status]?.[1] || 'gray'} />
                </div>
                {q.lines.map(l => (
                  <div key={l._id} className="text-xs flex justify-between gap-2">
                    <span>{l.qty} × {l.name} ({l.unitName}) @ {ugx(l.unitPriceUGX)}{l.productId ? ' · shop stock' : l.supplyListingId ? ' · from seller' : ''}</span>
                    {l.marginUGX != null && <span className={l.marginUGX < 0 ? 'text-red' : 'text-green'}>{l.marginUGX >= 0 ? '+' : ''}{ugx(l.marginUGX)}</span>}
                  </div>
                ))}
                <div className="text-[11px] text-text-muted mt-1">
                  Delivery {ugx(q.deliveryFeeUGX)} · valid until {when(q.validUntil)}
                  {q.marginUGX != null && <> · margin <span className="text-green">{ugx(q.marginUGX)}</span>{q.marginComplete ? '' : ' (some costs unknown)'}</>}
                  {q.buyerNote && <> · buyer: “{q.buyerNote}”</>}
                </div>
                {q.order && <div className="text-xs text-cyan font-mono mt-1">→ {q.order.orderRef} · {STATUS_LABEL[q.order.status]}</div>}
                {q.status === 'sent' && (
                  <div className="flex gap-2 mt-2">
                    {r.whatsapp && <a href={buyerWa(quoteText(r, q))} target="_blank" rel="noopener noreferrer"><Button size="xs" variant="success">WhatsApp the quote</Button></a>}
                    <Button size="xs" variant="ghost" onClick={() => withdraw(q)}>Withdraw</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {open && (
          <>
            <Matches r={r} onProduct={addProduct} onSupply={addSupply} />
            <QuoteBuilder r={r} lines={lines} setLines={setLines} onAddFree={addFree}
              onSent={q => { setLines([]); setLastSent(q); load(); }} />
            {lastSent && r.whatsapp && (
              <p className="text-xs">Sent {lastSent.ref}. The buyer gets an in-app notice —
                <a className="text-green font-semibold" href={buyerWa(quoteText(r, lastSent))} target="_blank" rel="noopener noreferrer"> also send it on WhatsApp ↗</a></p>
            )}
          </>
        )}
      </div>
      <NoteModal open={!!prompt} {...(prompt || {})} onClose={() => setPrompt(null)} onConfirm={note => prompt.run(note)} />
      {notesOpen && <NotesModal title={`Private notes — ${r.ref}`} initial={r.adminNotes}
        hint="Private: buyer's payment habits, follow-ups, who to source from. The buyer never sees this."
        onClose={() => setNotesOpen(false)} onSave={async t => { await marketAPI.requestNotes(r._id, t); toast.success('Notes saved'); load(); }} />}
    </Modal>
  );
}

const quoteText = (r, q) => [
  `Hello ${r.buyer?.name || ''}, DwiSend quote ${q.ref} for your request ${r.ref}:`,
  ...q.lines.map(l => `- ${l.qty} × ${l.name} (${l.unitName}) @ ${ugx(l.unitPriceUGX)} = ${ugx(l.lineTotalUGX)}`),
  q.deliveryFeeUGX ? `- Delivery: ${ugx(q.deliveryFeeUGX)}` : null,
  `Total: ${ugx(q.totalUGX)}. Valid until ${when(q.validUntil)}.`,
  q.terms ? `Terms: ${q.terms}` : null,
  `Accept and pay in the app: ${window.location.origin}/market?tab=requests`,
].filter(Boolean).join('\n');

function Matches({ r, onProduct, onSupply }) {
  const { supply, products } = r.matches;
  return (
    <div>
      <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider mb-2">What could fill it — {r.categoryName} (best name matches first)</div>
      {supply.length === 0 && products.length === 0 && <p className="text-xs text-text-muted">Nothing in this category yet. Add a free line below, or source it and add it as a product.</p>}
      {products.map(p => (
        <div key={p._id} className="text-xs border-b border-border py-1.5">
          <span className="text-text-primary font-semibold">📦 {p.name}</span> <span className="text-text-muted">shop · {p.stockQty.toLocaleString()} {p.baseUnit} in stock</span>
          <div className="flex gap-2 flex-wrap mt-1">
            {p.variants.map(v => (
              <Button key={v._id} size="xs" variant="ghost" onClick={() => onProduct(p, v)}>
                + {v.unitName} @ {v.priceUGX != null ? ugx(v.priceUGX) : 'no price'}{v.costUGX != null ? ` (cost ${ugx(v.costUGX)})` : ''}
              </Button>
            ))}
          </div>
        </div>
      ))}
      {supply.map(s => (
        <div key={s._id} className="text-xs border-b border-border py-1.5 flex items-start justify-between gap-2 flex-wrap">
          <div>
            <span className="text-text-primary font-semibold">🌾 {s.productName}</span> <span className="text-text-muted">{s.ref} · {LISTING_STATUS[s.status][0]}</span>
            <div className="text-text-muted">{s.quantity.toLocaleString()} × {s.unitName} @ {ugx(s.priceUGX)}{s.baseQty > 1 ? ` (${ugx(s.pricePerBaseUGX)}/${s.baseUnit})` : ''} · {s.location}
              {s.readyDate ? ` · ready ${fmtDay(s.readyDate)}` : ''} · {s.seller?.businessName}{s.seller?.status !== 'approved' ? ` (seller ${s.seller?.status})` : ''}</div>
          </div>
          <div className="flex gap-2 items-center">
            <ContactButtons phone={s.seller?.phone} whatsapp={s.seller?.whatsapp} text={`Hello ${s.seller?.businessName || ''}, this is DwiSend about your listing ${s.ref} (${s.productName}). `} />
            <Button size="xs" variant="ghost" onClick={() => onSupply(s)}>+ Quote it</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function QuoteBuilder({ r, lines, setLines, onAddFree, onSent }) {
  const [fee, setFee]     = useState(r.deliveryZoneFeeUGX ?? 0);
  const [terms, setTerms] = useState('');
  const [days, setDays]   = useState(3);
  const [busy, setBusy]   = useState(false);
  const setLine = (i, k, v) => setLines(ls => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const num = v => (String(v).trim() === '' ? null : Number(v));
  const subtotal = lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unitPriceUGX) || 0), 0);
  const known = lines.filter(l => num(l.costUGX) != null);
  const margin = known.reduce((s, l) => s + (Number(l.qty) || 0) * ((Number(l.unitPriceUGX) || 0) - Number(l.costUGX)), 0);

  const send = async () => {
    setBusy(true);
    try {
      const body = {
        lines: lines.map(l => ({ productId: l.productId, variantId: l.variantId, supplyListingId: l.supplyListingId, name: l.name, unitName: l.unitName,
          qty: Number(l.qty), unitPriceUGX: Number(l.unitPriceUGX), costUGX: l.kind === 'free' ? num(l.costUGX) : undefined })),
        deliveryFeeUGX: Number(fee || 0), terms, validDays: Number(days),
      };
      const res = await marketAPI.sendQuote(r._id, body);
      toast.success(`Quote ${res.quote.ref} sent to the buyer`);
      onSent(res.quote);
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-cyan/40 rounded-xl p-3 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs font-semibold text-cyan">New quote{r.quotes.some(q => q.status === 'sent') ? ' (replaces the one waiting for the buyer)' : ''}</div>
        <Button size="xs" variant="ghost" onClick={onAddFree}>+ Free line</Button>
      </div>
      {lines.length === 0 && <p className="text-xs text-text-muted">Add lines from the matches above, or a free line (anything you'll source yourself).</p>}
      {lines.map((l, i) => {
        const cost = num(l.costUGX); const price = Number(l.unitPriceUGX) || 0;
        const short = l.kind === 'product' && l.inStock != null && Number(l.qty) > l.inStock;
        return (
          <div key={i} className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-4"><Input label={i === 0 ? 'Line (buyer sees)' : undefined} value={l.name} onChange={e => setLine(i, 'name', e.target.value)} /></div>
            <div className="col-span-2">{l.kind === 'product'
              ? <div className="pb-3 text-xs">{i === 0 && <div className="text-xs font-bold text-text-muted uppercase tracking-wider mb-1.5">Unit</div>}{l.unitName}</div>
              : <Input label={i === 0 ? 'Unit' : undefined} value={l.unitName} onChange={e => setLine(i, 'unitName', e.target.value)} />}</div>
            <div className="col-span-1"><Input label={i === 0 ? 'Qty' : undefined} type="number" min="1" step="1" value={l.qty} onChange={e => setLine(i, 'qty', e.target.value)} /></div>
            <div className="col-span-2"><Input label={i === 0 ? 'Price each' : undefined} type="number" min="1" step="1" value={l.unitPriceUGX} onChange={e => setLine(i, 'unitPriceUGX', e.target.value)} /></div>
            <div className="col-span-2">{l.kind === 'free'
              ? <Input label={i === 0 ? 'Cost (private)' : undefined} type="number" min="0" step="1" value={l.costUGX} onChange={e => setLine(i, 'costUGX', e.target.value)} />
              : <div className="pb-3 text-[11px] text-text-muted">{i === 0 && <div className="text-xs font-bold uppercase tracking-wider mb-1.5">Cost</div>}{cost != null ? ugx(cost) : '—'}</div>}</div>
            <div className="col-span-1 pb-3 text-xs flex items-center gap-1">
              <button className="text-text-muted hover:text-red" title="Remove" onClick={() => setLines(ls => ls.filter((_, j) => j !== i))}>×</button>
            </div>
            <div className="col-span-12 -mt-1 text-[11px] text-text-muted">
              {l.kind === 'product' ? `Shop stock — held when the buyer accepts` : l.kind === 'supply' ? `From seller listing ${l.ref} — no shop stock` : 'Free line — no shop stock'}
              {' · '}line {ugx((Number(l.qty) || 0) * price)}
              {cost != null && <span className={price - cost < 0 ? 'text-red' : 'text-green'}> · margin {ugx((Number(l.qty) || 0) * (price - cost))}</span>}
              {short && <span className="text-red"> · only {l.inStock} in stock</span>}
            </div>
          </div>
        );
      })}
      <div className="grid sm:grid-cols-3 gap-3">
        <Input label="Delivery fee (UGX)" type="number" min="0" step="1" value={fee} onChange={e => setFee(e.target.value)} />
        <Input label="Valid for (days)" type="number" min="1" max="30" step="1" value={days} onChange={e => setDays(e.target.value)} />
        <div className="text-sm pt-5">
          <div>Total <b className="text-text-primary">{ugx(subtotal + (Number(fee) || 0))}</b></div>
          {known.length > 0 && <div className="text-[11px] text-green">Margin {ugx(margin)}{known.length < lines.length ? ' (some costs unknown)' : ''}</div>}
        </div>
      </div>
      <Input label="Terms buyer sees (optional)" value={terms} onChange={e => setTerms(e.target.value)} placeholder="e.g. Delivery within 3 days of payment. Price includes bags." />
      {r.targetPriceUGX && <p className="text-[11px] text-text-muted">Buyer's target: {ugx(r.targetPriceUGX)} per {r.unitName}.</p>}
      <Button loading={busy} disabled={!lines.length} onClick={send}>Send quote to buyer</Button>
    </div>
  );
}

// ── Sellers (Phase 2): vet who sells to DWI ──────────────────────────────────
const SELLER_COLOR = { pending: 'amber', approved: 'green', rejected: 'red', suspended: 'red' };
const SELLER_ORDER = { pending: 0, approved: 1, suspended: 2, rejected: 3 };

function ContactButtons({ phone, whatsapp, text }) {
  return (
    <div className="flex gap-2 mt-1">
      {phone && <a className="text-cyan text-xs" href={`tel:${phone.replace(/\s/g, '')}`}>📞 {phone}</a>}
      {whatsapp && <a className="text-green text-xs" target="_blank" rel="noopener noreferrer" href={waLink(whatsapp, text)}>WhatsApp</a>}
    </div>
  );
}

function NotesModal({ title, initial, hint, onClose, onSave }) {
  const [text, setText] = useState(initial || '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await onSave(text); onClose(); } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={title} size="sm">
      <div className="space-y-3">
        <p className="text-xs text-text-muted">{hint || 'Private — only admins see this.'}</p>
        <textarea rows={6} maxLength={2000} value={text} onChange={e => setText(e.target.value)} autoFocus
          className="w-full bg-bg-elevated border border-border rounded-xl px-3 py-2 text-sm text-text-primary outline-none focus:border-cyan" />
        <div className="flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1" loading={busy} onClick={save}>Save</Button>
        </div>
      </div>
    </Modal>
  );
}

function SellersSection({ onListings }) {
  const [status, setStatus] = useState('');
  const [q, setQ]           = useState('');
  const [data, setData]     = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [notesFor, setNotesFor] = useState(null);

  const load = useCallback(async () => {
    try { setData(await marketAPI.adminSellers({ status: status || undefined, q: q.trim() || undefined })); }
    catch (err) { toast.error(err.message); }
  }, [status, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const review = async (s, action, reason) => {
    try { await marketAPI.reviewSeller(s._id, action, reason); toast.success({ approve: 'Seller approved — they are told in the app', reject: 'Application rejected', suspend: 'Seller suspended' }[action]); load(); }
    catch (err) { toast.error(err.message); throw err; }
  };
  const ask = (s, action) => setPrompt({
    title: `${action === 'reject' ? 'Reject' : 'Suspend'} ${s.businessName}`, required: true,
    confirmLabel: action === 'reject' ? 'Reject' : 'Suspend',
    hint: action === 'reject' ? 'The seller sees this reason and can update their details to apply again.'
      : 'The seller sees this reason and can no longer add or edit listings. Products already in the shop stay until you change them.',
    run: note => review(s, action, note),
  });

  const sellers = (data?.sellers || []).slice().sort((a, b) => SELLER_ORDER[a.status] - SELLER_ORDER[b.status]);
  return (
    <Card>
      <SectionHeader title={`Sellers${data?.pending ? ` — ${data.pending} waiting for review` : ''}`} action={
        <div className="flex gap-2 items-center">
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, phone, district, goods" />
          <Select value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="pending">Waiting for review</option>
            <option value="approved">Approved</option>
            <option value="suspended">Suspended</option>
            <option value="rejected">Rejected</option>
          </Select>
        </div>
      } />
      <p className="text-[11px] text-text-muted mb-3">
        People who want to sell to DWI. Vet them (call, visit, check quality) before approving — only approved sellers' listings can be
        approved or published. Buyers never see sellers. Seller sign-up has its own switch in Settings.
      </p>
      {!data ? <div className="py-10 flex justify-center"><Spinner /></div>
        : sellers.length === 0 ? <Empty icon="🧑‍🌾" title="No sellers yet" description="When seller sign-up is ON, applications appear here and you get an SMS." />
        : (
          <Table headers={['Seller', 'Contact', 'Sells', 'Listings', 'Status', '']}>
            {sellers.map(s => {
              const c = s.listingCounts || {};
              const open = (c.submitted || 0) + (c.approved || 0) + (c.published || 0);
              return (
                <tr key={s._id} className="align-top">
                  <Td>
                    <div className="text-text-primary font-semibold">{s.businessName}</div>
                    <div className="text-[11px] text-text-muted">{s.district}{s.location ? ` · ${s.location}` : ''}</div>
                    <div className="text-[11px] text-text-muted">Account: {s.userId?.name || '—'} · applied {when(s.createdAt)}</div>
                    {s.adminNotes && <div className="text-[11px] text-amber mt-1 max-w-[220px]">🔒 {s.adminNotes}</div>}
                  </Td>
                  <Td><ContactButtons phone={s.phone} whatsapp={s.whatsapp} text={`Hello ${s.businessName}, this is DwiSend. `} /></Td>
                  <Td><div className="text-xs max-w-[240px] whitespace-pre-line">{s.supplies}</div></Td>
                  <Td>
                    <div className="text-xs">{open} open{c.submitted ? ` · ${c.submitted} to review` : ''}</div>
                    <Button size="xs" variant="ghost" onClick={() => onListings(s)}>View listings</Button>
                  </Td>
                  <Td>
                    <Badge label={s.status} color={SELLER_COLOR[s.status]} />
                    {s.statusReason && <div className="text-[11px] text-text-muted mt-1 max-w-[160px]">{s.statusReason}</div>}
                  </Td>
                  <Td>
                    <div className="flex flex-col gap-1 min-w-[110px]">
                      {['pending', 'rejected', 'suspended'].includes(s.status) && <Button size="xs" variant="success" onClick={() => review(s, 'approve').catch(() => {})}>{s.status === 'suspended' ? 'Reinstate' : 'Approve'}</Button>}
                      {s.status === 'pending' && <Button size="xs" variant="ghost" onClick={() => ask(s, 'reject')}>Reject</Button>}
                      {s.status === 'approved' && <Button size="xs" variant="ghost" onClick={() => ask(s, 'suspend')}>Suspend</Button>}
                      <Button size="xs" variant="secondary" onClick={() => setNotesFor(s)}>🔒 Notes</Button>
                      <MessageButton target={{ userId: s.userId?._id, name: s.businessName, topic: 'selling', about: 'selling to DWI' }} />
                    </div>
                  </Td>
                </tr>
              );
            })}
          </Table>
        )}
      <NoteModal open={!!prompt} {...(prompt || {})} onClose={() => setPrompt(null)} onConfirm={note => prompt.run(note)} />
      {notesFor && <NotesModal title={`Private notes — ${notesFor.businessName}`} initial={notesFor.adminNotes}
        hint="Private: vetting, quality visit by your delegate, terms agreed. The seller never sees this."
        onClose={() => setNotesFor(null)} onSave={async t => { await marketAPI.sellerNotes(notesFor._id, t); toast.success('Notes saved'); load(); }} />}
    </Card>
  );
}

// ── Supply (Phase 2): every seller listing — your private supply catalog ─────
// "⚠ check size": the listing's size doesn't match what the seller wrote (sizeHints.js). A hint only.
function SizeCheck({ l }) {
  const warnings = sizeWarnings({ productName: l.productName, notes: l.notes, unit: { name: l.unitName, baseUnit: l.baseUnit, baseQty: l.baseQty } });
  if (!warnings.length) return null;
  return <span title={warnings.join('\n')} data-testid="check-size"><Badge label="⚠ check size" color="amber" /></span>;
}

function SupplySection({ seller, onClearSeller }) {
  const [filter, setFilter] = useState({ status: 'open', categoryId: '', q: '' });
  const [data, setData]     = useState(null);
  const [categories, setCategories] = useState([]);
  const [prompt, setPrompt] = useState(null);
  const [detail, setDetail] = useState(null);
  const [notesFor, setNotesFor] = useState(null);
  const [publishFor, setPublishFor] = useState(null);
  const [editFor, setEditFor] = useState(null);   // admin direct-edit

  useEffect(() => { marketAPI.listCatalog('category').then(r => setCategories(r.items || [])).catch(err => toast.error(err.message)); }, []);
  const load = useCallback(async () => {
    try {
      setData(await marketAPI.adminSupply({ status: filter.status || undefined, categoryId: filter.categoryId || undefined,
        sellerId: seller?._id, q: filter.q.trim() || undefined }));
    } catch (err) { toast.error(err.message); }
  }, [filter, seller]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const review = async (l, action, reason) => {
    try {
      await marketAPI.reviewListing(l._id, action, reason);
      toast.success({ approve: 'Listing approved — the seller is told', reject: 'Listing rejected', sold_out: 'Marked sold out' }[action]);
      setDetail(null); load();
    } catch (err) { toast.error(err.message); throw err; }
  };
  const seen = async l => { try { await marketAPI.listingNotes(l._id, { seen: true }); load(); } catch (err) { toast.error(err.message); } };
  const setF = (k, v) => setFilter(f => ({ ...f, [k]: v }));
  const c = data?.counts || {};

  return (
    <Card>
      <SectionHeader title="Supply — what sellers offer DWI" action={
        <div className="flex gap-2 items-center flex-wrap">
          <Input value={filter.q} onChange={e => setF('q', e.target.value)} placeholder="Search product, place, SUP-ref" />
          <Select value={filter.categoryId} onChange={e => setF('categoryId', e.target.value)}>
            <option value="">All categories</option>
            {categories.map(cat => <option key={cat._id} value={cat._id}>{cat.name}</option>)}
          </Select>
          <Select value={filter.status} onChange={e => setF('status', e.target.value)}>
            <option value="open">Open (to review, approved, in shop)</option>
            <option value="submitted">To review ({c.submitted || 0})</option>
            <option value="approved">Approved ({c.approved || 0})</option>
            <option value="published">In shop ({c.published || 0})</option>
            <option value="sold_out">Sold out ({c.sold_out || 0})</option>
            <option value="rejected">Rejected ({c.rejected || 0})</option>
            <option value="withdrawn">Withdrawn ({c.withdrawn || 0})</option>
            <option value="">All</option>
          </Select>
        </div>
      } />
      <p className="text-[11px] text-text-muted mb-3">
        Private to admins. Approve a listing once you've checked it, then <b>Publish to shop</b>: the seller's price becomes your cost and
        DWI's margin is added automatically. Buyers only ever see DWI.
        {data?.updated ? <span className="text-amber"> {data.updated} listing(s) changed by the seller since you last looked.</span> : null}
        {data?.openObjections ? <span className="text-amber"> ⚠ {data.openObjections} seller objection(s) to your edits to review (your edits stay in place).</span> : null}
      </p>
      {seller && (
        <div className="mb-3 text-xs flex items-center gap-2">Showing <b className="text-text-primary">{seller.businessName}</b> only
          <Button size="xs" variant="ghost" onClick={onClearSeller}>× show all sellers</Button></div>
      )}
      {!data ? <div className="py-10 flex justify-center"><Spinner /></div>
        : data.listings.length === 0 ? <Empty icon="🌾" title="Nothing here" description="Seller listings appear here; you get an SMS for each new one." />
        : (
          <Table headers={['', 'Product', 'Offer (seller price)', 'Where / when', 'Seller', 'Status', '']}>
            {data.listings.map(l => {
              const [label, color] = LISTING_STATUS[l.status];
              const changed = !l.seenByAdmin && l.sellerUpdatedAt && l.status !== 'submitted';
              return (
                <tr key={l._id} className="align-top">
                  <Td>{l.photos?.[0] ? <SupplyPhoto name={l.photos[0]} className="w-12 h-12" onClick={() => setDetail(l)} />
                    : <div className="w-12 h-12 rounded-lg bg-bg-elevated border border-border flex items-center justify-center">🌾</div>}</Td>
                  <Td>
                    <button className="text-text-primary font-semibold hover:underline text-left" onClick={() => setDetail(l)}>{l.productName}</button>
                    <div className="text-[11px] text-text-muted">{l.ref} · {l.categoryName}</div>
                    {changed && <Badge label="Updated by seller" color="amber" />}
                    <SizeCheck l={l} />
                    {l.openObjections > 0 && <Badge label={`⚠ ${l.openObjections} objection${l.openObjections > 1 ? 's' : ''}`} color="amber" />}
                    {l.product && <div className="text-[11px] text-green mt-1">→ {l.product.name}{l.product.isActive ? '' : ' (removed)'}</div>}
                  </Td>
                  <Td>
                    <div className="text-xs">{l.quantity.toLocaleString()} × {l.unitName}</div>
                    <div className="text-text-primary font-semibold text-sm">{ugx(l.priceUGX)} <span className="text-[11px] text-text-muted font-normal">each</span></div>
                    {l.baseQty > 1 && <div className="text-[11px] text-text-muted">= {ugx(l.pricePerBaseUGX)}/{l.baseUnit} · {l.totalBaseQty.toLocaleString()} {l.baseUnit}</div>}
                  </Td>
                  <Td><div className="text-xs max-w-[160px]">{l.location}</div><div className="text-[11px] text-text-muted">{l.readyDate ? `ready ${new Date(l.readyDate).toLocaleDateString('en-GB')}` : 'ready now'}</div></Td>
                  <Td>
                    <div className="text-xs text-text-primary">{l.seller?.businessName}</div>
                    {l.seller?.status !== 'approved' && <Badge label={`seller ${l.seller?.status}`} color={SELLER_COLOR[l.seller?.status] || 'gray'} />}
                    <ContactButtons phone={l.seller?.phone} whatsapp={l.seller?.whatsapp}
                      text={`Hello ${l.seller?.businessName || ''}, this is DwiSend about your listing ${l.ref} (${l.productName}). `} />
                  </Td>
                  <Td><Badge label={label} color={color} />{l.adminNotes && <div className="text-[11px] text-amber mt-1 max-w-[140px]">🔒 {l.adminNotes}</div>}</Td>
                  <Td>
                    <div className="flex flex-col gap-1 min-w-[120px]">
                      {['submitted', 'rejected'].includes(l.status) && <Button size="xs" variant="success" onClick={() => review(l, 'approve').catch(() => {})}>Approve</Button>}
                      {['approved', 'published'].includes(l.status) && <Button size="xs" variant="primary" onClick={() => setPublishFor(l)}>{l.status === 'published' ? 'Update shop' : 'Publish to shop'}</Button>}
                      {['submitted', 'approved'].includes(l.status) && <Button size="xs" variant="ghost" onClick={() => setPrompt({
                        title: `Reject ${l.productName} (${l.ref})`, required: true, confirmLabel: 'Reject',
                        hint: 'The seller sees this reason and can edit the listing and send it again.', run: note => review(l, 'reject', note),
                      })}>Reject</Button>}
                      {['approved', 'published'].includes(l.status) && <Button size="xs" variant="ghost" onClick={() => review(l, 'sold_out').catch(() => {})}>Sold out</Button>}
                      {changed && <Button size="xs" variant="ghost" onClick={() => seen(l)}>✓ Seen</Button>}
                      {l.status !== 'withdrawn' && <Button size="xs" variant="secondary" onClick={() => setEditFor(l)}>✏️ Edit (DWI)</Button>}
                      <Button size="xs" variant="secondary" onClick={() => setNotesFor(l)}>🔒 Notes</Button>
                      <MessageButton target={{ userId: l.userId, name: l.seller?.businessName, topic: 'selling', listingId: l._id, about: `listing ${l.ref} (${l.productName})` }} />
                    </div>
                  </Td>
                </tr>
              );
            })}
          </Table>
        )}
      {detail && <ListingDetail l={detail} onClose={() => setDetail(null)} onEdit={() => { setEditFor(detail); setDetail(null); }} />}
      {editFor && <AdminEditListingModal l={editFor} onClose={() => setEditFor(null)} onSaved={() => { setEditFor(null); load(); }} />}
      <NoteModal open={!!prompt} {...(prompt || {})} onClose={() => setPrompt(null)} onConfirm={note => prompt.run(note)} />
      {notesFor && <NotesModal title={`Private notes — ${notesFor.ref}`} initial={notesFor.adminNotes}
        hint="Private: quality check, price agreed, pickup. The seller never sees this."
        onClose={() => setNotesFor(null)} onSave={async t => { await marketAPI.listingNotes(notesFor._id, { adminNotes: t, seen: true }); toast.success('Notes saved'); load(); }} />}
      {publishFor && <PublishModal listing={publishFor} onClose={() => setPublishFor(null)} onDone={() => { setPublishFor(null); load(); }} />}
    </Card>
  );
}

function ListingDetail({ l, onClose, onEdit }) {
  const [big, setBig] = useState(null);
  return (
    <Modal open onClose={onClose} title={`${l.productName} — ${l.ref}`} size="lg">
      <div className="space-y-4 text-sm">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Seller" value={`${l.seller?.businessName || '—'} (${l.seller?.district || ''})`} />
          <Field label="Status" value={LISTING_STATUS[l.status][0]} />
          <Field label="Quantity" value={`${l.quantity.toLocaleString()} × ${l.unitName}${l.baseQty > 1 ? ` (${l.totalBaseQty.toLocaleString()} ${l.baseUnit})` : ''}`} />
          <Field label="Seller price" value={`${ugx(l.priceUGX)} per ${l.unitName} · all: ${ugx(l.priceUGX * l.quantity)}`} />
          <Field label="Where" value={l.location} />
          <Field label="Ready" value={l.readyDate ? new Date(l.readyDate).toLocaleDateString('en-GB') : 'Now'} />
          {l.notes && <Field label="Seller's notes" value={l.notes} />}
          {l.weightGrams != null && <Field label="Weight of one" value={`${(l.weightGrams / 1000).toLocaleString('en-US', { maximumFractionDigits: 3 })} kg`} />}
          {l.statusReason && <Field label="Reason given" value={l.statusReason} />}
          <Field label="Listed" value={when(l.createdAt)} />
          {l.sellerUpdatedAt && <Field label="Seller last changed" value={when(l.sellerUpdatedAt)} />}
        </div>
        <ContactButtons phone={l.seller?.phone} whatsapp={l.seller?.whatsapp} text={`Hello ${l.seller?.businessName || ''}, this is DwiSend about your listing ${l.ref} (${l.productName}). `} />
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider">DWI edits (the seller sees these)</div>
            {l.status !== 'withdrawn' && <Button size="xs" variant="secondary" onClick={onEdit}>✏️ Edit (DWI)</Button>}
          </div>
          <ListingEditHistory listingId={l._id} />
        </div>
        {l.photos?.length > 0 && (
          <div>
            <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider mb-2">Photos (private) — tap to enlarge</div>
            <div className="flex gap-2 flex-wrap">{l.photos.map(p => <SupplyPhoto key={p} name={p} className="w-24 h-24" onClick={setBig} />)}</div>
            {big && <img src={big} alt="" className="mt-3 max-h-[60vh] rounded-xl border border-border" onClick={() => setBig(null)} />}
          </div>
        )}
      </div>
    </Modal>
  );
}

// "Publish to shop": a new DWI product, or add this seller's offer to an
// existing product. Seller price → cost; price = cost + margin (rounded up).
function PublishModal({ listing, onClose, onDone }) {
  const [products, setProducts]     = useState(null);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings]     = useState(null);
  const [f, setF] = useState({
    mode: listing.productId ? 'existing' : 'new', productId: listing.productId || '',
    name: listing.productName, categoryId: listing.categoryId, description: '', marginPct: '',
    photos: [], addStock: !listing.productId,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  useEffect(() => {
    Promise.all([marketAPI.listProducts(), marketAPI.listCatalog('category'), marketAPI.getSettings()])
      .then(([p, c, s]) => { setProducts(p.products || []); setCategories(c.items || []); setSettings(s.settings); })
      .catch(err => toast.error(err.message));
  }, []);
  if (!products || !settings) return <Modal open onClose={onClose} title="Publish to shop"><div className="py-10 flex justify-center"><Spinner /></div></Modal>;

  const matching = products.filter(p => p.baseUnit === listing.baseUnit);
  const target = matching.find(p => p._id === f.productId);
  const category = categories.find(cat => cat._id === (f.mode === 'new' ? f.categoryId : target?.categoryId));
  const margin = marginFor(f.mode === 'new' ? f.marginPct : target?.marginPct, category, settings);
  const round = settings.priceRoundUGX || 1;
  const price = margin.pct != null ? autoPrice(listing.priceUGX, margin.pct, round) : null;
  // Only what this listing hasn't already put in stock — re-publishing never adds it twice.
  const addQty = Math.max(0, listing.quantity * (listing.baseQty || 0) - (listing.stockAddedQty || 0));
  const room = f.mode === 'existing' && target ? 6 - (target.photos?.length || 0) : 6;
  const togglePhoto = name => set('photos', f.photos.includes(name) ? f.photos.filter(x => x !== name)
    : f.photos.length >= room ? (toast.error(`Room for ${room} more photo(s)`), f.photos) : [...f.photos, name]);

  const go = async () => {
    if (f.mode === 'existing' && !f.productId) return toast.error('Choose the product');
    setBusy(true);
    try {
      const body = f.mode === 'existing'
        ? { productId: f.productId, photos: f.photos, addStock: f.addStock }
        : { name: f.name, categoryId: f.categoryId, description: f.description, photos: f.photos, addStock: f.addStock,
            marginPct: f.marginPct === '' ? null : Number(f.marginPct) };
      const r = await marketAPI.publishListing(listing._id, body);
      toast.success(`In the shop: ${r.product.name}. The seller is told (not your price).`);
      onDone();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Publish to shop — ${listing.productName} (${listing.ref})`} size="lg">
      <div className="space-y-4">
        <div className="text-xs text-text-muted">
          Seller offers {listing.quantity.toLocaleString()} × {listing.unitName} at <b className="text-text-primary">{ugx(listing.priceUGX)}</b> each.
          This becomes your private <b>cost</b> for the "{listing.unitName}" size, auto-priced at cost + margin.
          The seller's name and contact go into the product's private supplier section — buyers see only DWI.
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant={f.mode === 'new' ? 'primary' : 'secondary'} onClick={() => set('mode', 'new')}>New product</Button>
          <Button size="sm" variant={f.mode === 'existing' ? 'primary' : 'secondary'} disabled={!matching.length} onClick={() => set('mode', 'existing')}>
            Add to an existing product{matching.length ? '' : ' (none sold by ' + listing.baseUnit + ')'}
          </Button>
        </div>
        {f.mode === 'new' ? (
          <div className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <Input label="Name buyers see" value={f.name} onChange={e => set('name', e.target.value)} />
              <Select label="Category" value={f.categoryId} onChange={e => set('categoryId', e.target.value)}>
                {categories.filter(cat => cat.isActive || cat._id === f.categoryId).map(cat => <option key={cat._id} value={cat._id}>{cat.name}</option>)}
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-text-secondary">Description buyers see (optional)</label>
              <textarea rows={2} value={f.description} onChange={e => set('description', e.target.value)} placeholder="Grade, variety, packing… (don't name the seller)"
                className="mt-1 w-full bg-bg-elevated border border-border rounded-xl px-3 py-2 text-sm text-text-primary outline-none focus:border-cyan" />
            </div>
            <Input label="Margin % for this product (blank = inherit)" type="number" min="0" step="0.1" value={f.marginPct} onChange={e => set('marginPct', e.target.value)} />
          </div>
        ) : (
          <Select label={`Product (sold by ${listing.baseUnit})`} value={f.productId} onChange={e => set('productId', e.target.value)}>
            <option value="">— choose —</option>
            {matching.map(p => <option key={p._id} value={p._id}>{p.name}{p.isActive ? '' : ' (removed — will be put back)'}</option>)}
          </Select>
        )}
        <div className={`text-sm rounded-xl border p-3 ${price != null ? 'border-green/40' : 'border-red/40'}`}>
          {price != null
            ? <>Buyers will pay <b className="text-text-primary">{ugx(price)}</b> per {listing.unitName} — cost {ugx(listing.priceUGX)} + {margin.pct}% (from {margin.from}), rounded up to {ugx(round)}. Your margin: <b className="text-green">{ugx(price - listing.priceUGX)}</b> per {listing.unitName}.</>
            : <span className="text-red">No margin set — set a default margin in Settings, on the category, or here.</span>}
          {f.mode === 'existing' && target?.variants?.some(v => v.unitId === listing.unitId) && <div className="text-[11px] text-amber mt-1">This product already sells "{listing.unitName}" — its cost and price will be replaced.</div>}
        </div>
        <label className="text-sm flex items-center gap-2">
          <input type="checkbox" checked={f.addStock} onChange={e => set('addStock', e.target.checked)} />
          Add the seller's quantity to shop stock (+{addQty.toLocaleString()} {listing.baseUnit})
        </label>
        {listing.stockAddedQty > 0 && <p className="text-[11px] text-amber -mt-2">{listing.stockAddedQty.toLocaleString()} {listing.baseUnit} from this listing is already in stock — only quantity above that is added.</p>}
        <p className="text-[11px] text-text-muted -mt-2">Tick when you've agreed to take it, so buyers can order it. Leave unticked to set stock yourself later.</p>
        {listing.photos?.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-text-secondary mb-2">Photos to show buyers (copied to the shop; untouched ones stay private)</div>
            <div className="flex gap-2 flex-wrap">
              {listing.photos.map(p => (
                <button key={p} onClick={() => togglePhoto(p)} className={`rounded-lg p-0.5 border-2 ${f.photos.includes(p) ? 'border-cyan' : 'border-transparent'}`}>
                  <SupplyPhoto name={p} className="w-20 h-20" />
                </button>
              ))}
            </div>
            <p className="text-[11px] text-text-muted mt-1">Skip photos showing the seller's name, phone or sign.</p>
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1" loading={busy} disabled={price == null} onClick={go}>{listing.status === 'published' ? 'Update shop' : 'Publish to shop'}</Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Small "note" prompt used by order actions ────────────────────────────────
function NoteModal({ open, title, hint, required, confirmLabel, onClose, onConfirm }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setNote(''); }, [open]);
  const go = async () => {
    if (required && !note.trim()) return toast.error('Please add a note');
    setBusy(true);
    try { await onConfirm(note.trim()); onClose(); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={title} size="sm">
      <div className="space-y-3">
        {hint && <p className="text-xs text-text-muted">{hint}</p>}
        <Input label={required ? 'Note (required)' : 'Note (optional)'} value={note} onChange={e => setNote(e.target.value)} autoFocus />
        <div className="flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>Back</Button>
          <Button className="flex-1" loading={busy} onClick={go}>{confirmLabel || 'Confirm'}</Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Orders ───────────────────────────────────────────────────────────────────
function OrdersSection() {
  const { newRefs, markSeen } = useOrderAlerts();
  const [status, setStatus]   = useState('');
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail]   = useState(null);
  const [prompt, setPrompt]   = useState(null);   // { title, hint, required, confirmLabel, run(note) }
  const [driverFor, setDriverFor] = useState(null); // { order, dispatch: bool }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await marketAPI.adminOrders({ status: status || undefined, limit: 100 });
      setOrders(r.docs || []);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    try { const r = await fn(); toast.success(okMsg); setDetail(d => (d && r.order && d._id === r.order._id ? r.order : d)); load(); }
    catch (err) { toast.error(err.message); throw err; }
  };

  const actionsFor = o => {
    const a = [];
    if (o.status === 'awaiting_payment') {
      a.push(['Check payment', 'secondary', () => act(() => marketAPI.syncPayment(o._id), 'Payment status refreshed')]);
      a.push(['Mark paid (bank/cash)', 'success', () => setPrompt({
        title: `Mark ${o.orderRef} paid`, required: true, confirmLabel: 'Mark paid',
        hint: `Only if you received ${ugx(o.totalUGX)} outside the app (bank, cash). The online payment link is closed so they can't pay twice. Note who paid and the bank/receipt ref.`,
        run: note => act(() => marketAPI.markPaidOffline(o._id, note), 'Marked paid'),
      })]);
      a.push(['Cancel', 'ghost', () => setPrompt({
        title: `Cancel ${o.orderRef}`, confirmLabel: 'Cancel order', hint: 'Stock goes back on the shelf.',
        run: note => act(() => marketAPI.setStatus(o._id, 'cancelled', note), 'Order cancelled'),
      })]);
    }
    if (o.status === 'paid') {
      a.push(['Mark dispatched', 'primary', () => setDriverFor({ order: o, dispatch: true })]);
      a.push(['Cancel (refund)', 'danger', () => setPrompt({
        title: `Cancel PAID order ${o.orderRef}`, required: true, confirmLabel: 'Cancel order',
        hint: `This order is paid (${ugx(o.totalUGX)}). Cancelling does NOT refund automatically — refund the buyer yourself. Say why.`,
        run: note => act(() => marketAPI.setStatus(o._id, 'cancelled', note), 'Order cancelled — remember the refund'),
      })]);
    }
    if (o.status === 'dispatched') a.push(['Mark delivered', 'primary', () => setPrompt({
      title: `Mark ${o.orderRef} delivered`, confirmLabel: 'Mark delivered', hint: 'e.g. who received it, any shortage or damage.',
      run: note => act(() => marketAPI.setStatus(o._id, 'delivered', note), 'Marked delivered'),
    })]);
    if (o.status === 'delivered')  a.push(['Complete', 'success', () => act(() => marketAPI.setStatus(o._id, 'completed'), 'Order completed')]);
    return a;
  };

  return (
    <Card>
      <SectionHeader title="Orders" action={
        <div className="flex gap-2 items-center">
          <Select value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
          <Button size="xs" variant="secondary" onClick={load}>Refresh</Button>
        </div>
      } />
      {loading ? <div className="py-10 flex justify-center"><Spinner /></div>
        : orders.length === 0 ? <Empty icon="🧾" title="No orders yet" description="New orders appear here and you get an SMS (set the phone in Settings)." />
        : (
          <Table headers={['Order', 'Buyer', 'Items', 'Delivery', 'Total', 'Status', '']}>
            {pinNew(orders, newRefs).map(o => (
              <tr key={o._id} className={newRefs.has(o.orderRef) ? 'align-top bg-red/10' : 'align-top'}>
                <Td>{newRefs.has(o.orderRef) && <span className="mr-1 px-1.5 rounded bg-red text-white text-[10px] font-black">NEW</span>}<button className="text-cyan font-mono text-xs hover:underline" onClick={() => { setDetail(o); markSeen(o.orderRef); }}>{o.orderRef}</button><div className="text-[11px] text-text-muted">{when(o.createdAt)}</div>{o.quoteRef && <div className="text-[10px] text-text-muted">from quote {o.quoteRef}</div>}</Td>
                <Td><div className="text-text-primary">{o.buyerName || '—'}</div><div className="text-[11px]">{o.buyerPhone}</div></Td>
                <Td>{o.items.map((i, k) => <div key={k} className="text-xs">{i.qty} × {i.name} ({i.unitName})</div>)}</Td>
                <Td><div className="text-xs">{o.deliveryZoneName || '—'}</div><div className="text-[11px] text-text-muted max-w-[180px] truncate">{o.deliveryAddress}</div></Td>
                <Td className="text-text-primary font-semibold whitespace-nowrap">{ugx(o.totalUGX)}{o.paymentMethod === 'offline' && <div className="text-[10px] text-text-muted">bank/cash</div>}</Td>
                <Td><Badge label={STATUS_LABEL[o.status]} color={STATUS_COLOR[o.status]} /></Td>
                <Td>
                  <div className="flex flex-col gap-1 items-stretch min-w-[150px]">
                    {actionsFor(o).map(([label, variant, fn]) => (
                      <Button key={label} size="xs" variant={variant} onClick={() => fn().catch(() => {})}>{label}</Button>
                    ))}
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        )}

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail ? `Order ${detail.orderRef}` : ''} size="lg">
        {detail && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Buyer" value={`${detail.buyerName || '—'} · ${detail.buyerPhone}`} />
              <Field label="Status" value={<Badge label={STATUS_LABEL[detail.status]} color={STATUS_COLOR[detail.status]} />} />
              <Field label="Delivery area" value={detail.deliveryZoneName || '—'} />
              <Field label="Address" value={detail.deliveryAddress} />
              {detail.notes && <Field label="Buyer notes" value={detail.notes} />}
              <Field label="Payment" value={detail.paymentMethod === 'offline' ? 'Bank / cash (marked by admin)' : detail.paymentMethod === 'charge' ? 'DWIPay / MoMo' : 'Not paid yet'} />
            </div>
            <Table headers={['Item', 'Qty', 'Price', 'Line']}>
              {detail.items.map((i, k) => (
                <tr key={k}><Td>{i.name} ({i.unitName})</Td><Td>{i.qty}</Td><Td>{ugx(i.unitPriceUGX)}</Td><Td>{ugx(i.lineTotalUGX)}</Td></tr>
              ))}
              <tr><Td /><Td /><Td>Delivery</Td><Td>{ugx(detail.deliveryFeeUGX)}</Td></tr>
              <tr><Td /><Td /><Td className="font-bold text-text-primary">Total</Td><Td className="font-bold text-text-primary">{ugx(detail.totalUGX)}</Td></tr>
            </Table>
            <MessageButton target={{ userId: detail.buyerId, name: detail.buyerName, topic: 'order', orderId: detail._id, about: `order ${detail.orderRef}` }} label="💬 Message the buyer about this order" />
            <DeliveryInfo order={detail} onEdit={DELIVERY_EDITABLE.includes(detail.status) ? () => setDriverFor({ order: detail, dispatch: false }) : null} />
            <div>
              <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider mb-2">History</div>
              {detail.statusHistory.map((h, k) => (
                <div key={k} className="text-xs flex gap-3"><span className="text-text-muted w-28">{when(h.at)}</span><span>{STATUS_LABEL[h.status]}</span>{h.note && <span className="text-text-muted">— {h.note}</span>}</div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      <NoteModal open={!!prompt} {...(prompt || {})} onClose={() => setPrompt(null)} onConfirm={note => prompt.run(note)} />
      {driverFor && (
        <DriverModal order={driverFor.order} dispatch={driverFor.dispatch} onClose={() => setDriverFor(null)}
          onSave={(delivery, note) => (driverFor.dispatch
            ? act(() => marketAPI.setStatus(driverFor.order._id, 'dispatched', note, delivery), 'Marked dispatched — buyer notified')
            : act(() => marketAPI.setDelivery(driverFor.order._id, delivery), 'Driver details saved'))} />
      )}
    </Card>
  );
}

// ── Delivery (manual): who is taking the order. Buyers see driver name,
// phone and vehicle; the trip cost is for your books only.
const DELIVERY_EDITABLE = ['paid', 'dispatched', 'delivered', 'completed'];
const VEHICLES = ['Boda boda', 'Car', 'Pickup', 'Fuso / truck', 'Own vehicle'];

function DeliveryInfo({ order, onEdit }) {
  const d = order.delivery || {};
  const has = d.driverName || d.driverPhone || d.vehicle || d.plate || d.tripCostUGX != null || d.note;
  if (!has && !onEdit) return null;
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider">Delivery</div>
        {onEdit && <Button size="xs" variant="secondary" onClick={onEdit}>{has ? 'Edit driver details' : '+ Driver details'}</Button>}
      </div>
      {has ? (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field label="Driver" value={[d.driverName, d.driverPhone].filter(Boolean).join(' · ') || '—'} />
          <Field label="Vehicle" value={[d.vehicle, d.plate].filter(Boolean).join(' · ') || '—'} />
          <Field label="Trip cost (private)" value={d.tripCostUGX != null ? ugx(d.tripCostUGX) : '—'} />
          {d.note && <Field label="Delivery note" value={d.note} />}
        </div>
      ) : <p className="text-xs text-text-muted">No driver recorded yet.</p>}
    </div>
  );
}

function DriverModal({ order, dispatch, onClose, onSave }) {
  const d = order.delivery || {};
  const [f, setF] = useState({
    driverName: d.driverName || '', driverPhone: d.driverPhone || '', vehicle: d.vehicle || '',
    plate: d.plate || '', tripCostUGX: d.tripCostUGX ?? '', note: d.note || '',
  });
  const [statusNote, setStatusNote] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await onSave({ ...f, tripCostUGX: f.tripCostUGX === '' ? null : Number(f.tripCostUGX) }, statusNote.trim());
      onClose();
    } catch { /* toast already shown */ } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={dispatch ? `Dispatch ${order.orderRef}` : `Driver details — ${order.orderRef}`} size="sm">
      <div className="space-y-3">
        <p className="text-xs text-text-muted">
          {dispatch ? 'All optional. The buyer is told the order is on the way, with the driver\'s name, phone and vehicle. ' : ''}
          Trip cost is for your records only — buyers never see it and nothing is paid through the app.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Driver name" value={f.driverName} onChange={e => set('driverName', e.target.value)} />
          <Input label="Driver phone" value={f.driverPhone} onChange={e => set('driverPhone', e.target.value)} placeholder="07XXXXXXXX" />
          <div>
            <Input label="Vehicle" value={f.vehicle} list="dwi-vehicles" onChange={e => set('vehicle', e.target.value)} placeholder="Boda boda" />
            <datalist id="dwi-vehicles">{VEHICLES.map(v => <option key={v} value={v} />)}</datalist>
          </div>
          <Input label="Plate" value={f.plate} onChange={e => set('plate', e.target.value)} placeholder="UBA 123X" />
          <Input label="Trip cost (UGX)" type="number" min="0" step="1" value={f.tripCostUGX} onChange={e => set('tripCostUGX', e.target.value)} />
          <Input label="Delivery note" value={f.note} onChange={e => set('note', e.target.value)} placeholder="e.g. 2 trips" />
        </div>
        {dispatch && <Input label="Note on this step (optional)" value={statusNote} onChange={e => setStatusNote(e.target.value)} placeholder="e.g. left store 9am" />}
        <div className="flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>Back</Button>
          <Button className="flex-1" loading={busy} onClick={save}>{dispatch ? 'Mark dispatched' : 'Save'}</Button>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider">{label}</div>
      <div className="text-text-primary">{value}</div>
    </div>
  );
}

// ── Products & stock ─────────────────────────────────────────────────────────
// Margin that applies to a product: its own → its category's → Settings default.
function marginFor(productMarginPct, category, settings) {
  if (productMarginPct !== '' && productMarginPct != null) return { pct: Number(productMarginPct), from: 'this product' };
  if (category?.marginPct != null) return { pct: category.marginPct, from: `category "${category.name}"` };
  if (settings?.defaultMarginPct != null) return { pct: settings.defaultMarginPct, from: 'the default in Settings' };
  return { pct: null, from: null };
}
// Same formula as the server (marketService.autoPrice): cost + margin, rounded UP.
const autoPrice = (cost, pct, round) => Math.ceil((cost * (100 + pct) / 100) / round - 1e-9) * round;
const waLink = (num, text) => `https://wa.me/${String(num).replace(/[^0-9]/g, '')}${text ? `?text=${encodeURIComponent(text)}` : ''}`;

function ProductsSection() {
  const [products, setProducts]     = useState([]);
  const [units, setUnits]           = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings]     = useState(null);
  const [catFilter, setCatFilter]   = useState('');
  const [loading, setLoading]       = useState(true);
  const [editing, setEditing]       = useState(null);   // product or {} for new
  const [stockFor, setStockFor]     = useState(null);
  const [drc, setDrc]               = useState(null);   // Uganda product id → its DRC listing (null = DRC shop not set up)

  const loadDrc = useCallback(() => dwisendShopAPI.adminProducts()
    .then(r => setDrc(new Map((r.products || []).filter(x => x.linkedMarketProductId).map(x => [String(x.linkedMarketProductId), x]))))
    .catch(() => setDrc(null)), []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, u, c, st] = await Promise.all([marketAPI.listProducts(), marketAPI.listCatalog('unit'), marketAPI.listCatalog('category'), marketAPI.getSettings()]);
      setProducts(p.products || []); setUnits(u.items || []); setCategories(c.items || []); setSettings(st.settings);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
    loadDrc();
  }, [loadDrc]);
  useEffect(() => { load(); }, [load]);

  // Public product page (Phase 4) — add ?src=<ad name> to see which ad works.
  const copyLink = p => navigator.clipboard.writeText(`${window.location.origin}/dwisend/p/${p.slug}`)
    .then(() => toast.success('Link copied — add ?src=facebook (etc.) to track an ad'))
    .catch(err => toast.error(err.message));
  const toggleActive = async p => {
    try {
      await marketAPI.updateProduct(p._id, { isActive: !p.isActive });
      toast.success(p.isActive ? 'Removed from the shop' : 'Back in the shop');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const shown = products.filter(p => !catFilter || p.categoryId === catFilter);

  return (
    <Card>
      <SectionHeader title="Products & Stock" action={
        <div className="flex gap-2 items-center">
          <Select value={catFilter} onChange={e => setCatFilter(e.target.value)}>
            <option value="">All categories</option>
            {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </Select>
          <Button size="sm" onClick={() => setEditing({})}>+ Add product</Button>
        </div>
      } />
      <p className="text-[11px] text-text-muted mb-3">Supplier, cost and margin are private — buyers only ever see DwiSend and the selling price.</p>
      {settings && settings.defaultMarginPct == null && (
        <p className="text-[11px] text-amber mb-3">No default margin set yet — auto prices need one (Settings, a category, or the product).</p>
      )}
      {loading ? <div className="py-10 flex justify-center"><Spinner /></div>
        : shown.length === 0 ? <Empty icon="📦" title="No products yet" description="Add your first product — e.g. Beans per kg and 50 kg bag, Tilapia per kg, or a Solar kit per piece." />
        : (
          <Table headers={['', 'Product', 'Sizes · price · margin', 'Supplier', 'Stock', 'Shop', '']}>
            {shown.map(p => (
              <tr key={p._id} className={`align-top ${p.isActive ? '' : 'opacity-50'}`}>
                <Td>{p.photos?.[0]
                  ? <img src={p.photos[0]} alt="" className="w-12 h-12 rounded-lg object-cover border border-border" />
                  : <div className="w-12 h-12 rounded-lg bg-bg-elevated border border-border flex items-center justify-center">📦</div>}</Td>
                <Td>
                  <div className="text-text-primary font-semibold">{p.name}</div>
                  <div className="text-[11px] text-text-muted">{p.categoryName}</div>
                  {p.priceMode === 'on_request' && <Badge label="Price on request" color="amber" />}
                </Td>
                <Td>{p.variants.map(v => (
                  <div key={v._id} className={`text-xs ${v.isActive && v.unitActive ? '' : 'line-through text-text-muted'}`}>
                    {v.unitName}: <span className="text-text-primary">{v.priceUGX != null ? ugx(v.priceUGX) : '—'}</span>
                    {v.priceAuto && <span className="text-cyan" title="Auto price: cost + margin"> ⚙</span>}
                    {v.marginUGX != null && (
                      <span className={v.marginUGX < 0 ? 'text-red' : 'text-green'}> · {v.marginUGX >= 0 ? '+' : ''}{ugx(v.marginUGX)}{v.marginOnCostPct != null ? ` (${v.marginOnCostPct}%)` : ''}</span>
                    )}
                  </div>
                ))}</Td>
                <Td>
                  {p.supplier?.name ? (
                    <div className="text-xs">
                      <div className="text-text-primary">{p.supplier.name}</div>
                      <div className="flex gap-2 mt-1">
                        {p.supplier.phone && <a className="text-cyan" href={`tel:${p.supplier.phone.replace(/\s/g, '')}`}>📞</a>}
                        {p.supplier.whatsapp && <a className="text-green" target="_blank" rel="noopener noreferrer" href={waLink(p.supplier.whatsapp, `Hello ${p.supplier.name}, this is DwiSend about ${p.name}. `)}>WhatsApp</a>}
                      </div>
                    </div>
                  ) : <span className="text-[11px] text-text-muted">—</span>}
                </Td>
                <Td>
                  <div className={`font-semibold ${p.stockQty === 0 ? 'text-red' : 'text-text-primary'}`}>{p.stockQty.toLocaleString()} {p.baseUnit}</div>
                  {p.variants.filter(v => v.baseQty > 1).map(v => <div key={v._id} className="text-[11px] text-text-muted">= {v.inStock} × {v.unitName}</div>)}
                </Td>
                <Td><Badge label={p.isActive ? 'In shop' : 'Removed'} color={p.isActive ? 'green' : 'gray'} /></Td>
                <Td>
                  <div className="flex flex-col gap-1 min-w-[110px]">
                    <Button size="xs" variant="secondary" onClick={() => setEditing(p)}>Edit</Button>
                    <Button size="xs" variant="primary" onClick={() => setStockFor(p)}>Stock</Button>
                    <Button size="xs" variant="ghost" onClick={() => toggleActive(p)}>{p.isActive ? 'Remove' : 'Restore'}</Button>
                    {p.slug && p.isActive && <Button size="xs" variant="ghost" onClick={() => copyLink(p)} title="Public page link for ads and WhatsApp (works once the page is ON)">🔗 Copy link</Button>}
                    {drc && <DrcListingButton product={p} listing={drc.get(String(p._id))} units={units} onSaved={loadDrc} />}
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      {editing && <ProductModal product={editing} units={units} categories={categories} settings={settings} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {stockFor && <StockModal product={stockFor} onClose={() => setStockFor(null)} onSaved={() => { setStockFor(null); load(); }} />}
    </Card>
  );
}

const CONDITION_OPTIONS = [['', '—'], ['new', 'New'], ['used', 'Used'], ['refurbished', 'Refurbished']];
const dateInput = d => (d ? new Date(d).toISOString().slice(0, 10) : '');

function ProductModal({ product, units, categories, settings, onClose, onSaved }) {
  const isNew = !product._id;
  const activeUnits = units.filter(u => u.isActive);
  const d = product.details || {};
  const sup = product.supplier || {};
  const [form, setForm] = useState(() => ({
    name: product.name || '',
    description: product.description || '',
    categoryId: product.categoryId || categories.find(c => c.isActive)?._id || '',
    photos: product.photos || [],
    stockQty: '',
    priceMode: product.priceMode || 'fixed',
    marginPct: product.marginPct ?? '',
    details: { brand: d.brand || '', condition: d.condition || '', warranty: d.warranty || '', origin: d.origin || '',
      perishable: Boolean(d.perishable), availableUntil: dateInput(d.availableUntil) },
    supplier: { name: sup.name || '', phone: sup.phone || '', whatsapp: sup.whatsapp || '', location: sup.location || '', notes: sup.notes || '', sellerId: sup.sellerId || null },
    variants: product.variants?.length
      ? product.variants.map(v => ({ _id: v._id, unitId: v.unitId, priceUGX: v.priceUGX ?? '', costUGX: v.costUGX ?? '',
          priceAuto: Boolean(v.priceAuto), minOrderQty: v.minOrderQty || 1, isActive: v.isActive }))
      : [],
  }));
  const [saving, setSaving]       = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setIn = (group, k, v) => setForm(f => ({ ...f, [group]: { ...f[group], [k]: v } }));
  const setVariant = (i, k, v) => setForm(f => ({ ...f, variants: f.variants.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const unitOf = id => units.find(u => u._id === id);
  const onRequest = form.priceMode === 'on_request';

  const category = categories.find(c => c._id === form.categoryId);
  const margin = marginFor(form.marginPct, category, settings);
  const round = settings?.priceRoundUGX || 1;

  // All sizes of a product share one stock unit (kg, piece, litre…).
  const baseUnit = form.variants.length ? unitOf(form.variants[0].unitId)?.baseUnit : null;
  const addableUnits = activeUnits.filter(u => !form.variants.some(v => v.unitId === u._id) && (!baseUnit || u.baseUnit === baseUnit));

  const upload = async e => {
    const files = [...(e.target.files || [])];
    e.target.value = '';
    if (form.photos.length + files.length > 6) return toast.error('Up to 6 photos per product');
    setUploading(true);
    try {
      for (const file of files) {
        const r = await marketAPI.uploadPhoto(file);
        setForm(f => ({ ...f, photos: [...f.photos, r.url] }));
      }
    } catch (err) { toast.error(err.message); }
    finally { setUploading(false); }
  };

  const save = async () => {
    const num = v => (String(v).trim() === '' ? null : Number(v));
    // Fixed price: a size with no price (and not auto-priced) is simply not sold.
    const variants = form.variants
      .filter(v => onRequest || v.priceAuto || String(v.priceUGX).trim() !== '')
      .map(v => ({ ...v, priceUGX: v.priceAuto ? null : num(v.priceUGX), costUGX: num(v.costUGX), minOrderQty: Number(v.minOrderQty) || 1 }));
    const body = {
      name: form.name, description: form.description, categoryId: form.categoryId, photos: form.photos, variants,
      priceMode: form.priceMode, marginPct: form.marginPct === '' ? null : Number(form.marginPct),
      details: { ...form.details, availableUntil: form.details.availableUntil || null },
      supplier: form.supplier,
    };
    if (isNew) body.stockQty = Number(form.stockQty || 0);
    setSaving(true);
    try {
      if (isNew) await marketAPI.createProduct(body); else await marketAPI.updateProduct(product._id, body);
      toast.success(isNew ? 'Product added' : 'Product saved');
      onSaved();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? 'Add product' : `Edit ${product.name}`} size="lg">
      <div className="space-y-5">
        <div className="space-y-4">
          <Input label="Name" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Beans (Nambale), Tilapia, Solar kit 100W" />
          <div>
            <label className="text-xs font-semibold text-text-secondary">Description</label>
            <textarea className="mt-1 w-full bg-bg-elevated border border-border rounded-xl px-3 py-2 text-sm text-text-primary outline-none focus:border-cyan" rows={3}
              value={form.description} onChange={e => set('description', e.target.value)} placeholder="Grade, size, specification, what's included…" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Select label="Category" value={form.categoryId} onChange={e => set('categoryId', e.target.value)}>
              {categories.filter(c => c.isActive || c._id === form.categoryId).map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </Select>
            <Select label="Pricing" value={form.priceMode} onChange={e => set('priceMode', e.target.value)}>
              <option value="fixed">Fixed price — buyers order and pay online</option>
              <option value="on_request">Price on request — buyers ask DWI</option>
            </Select>
          </div>
        </div>

        <div>
          <div className="text-xs font-semibold text-text-secondary mb-2">Photos ({form.photos.length}/6)</div>
          <div className="flex gap-2 flex-wrap">
            {form.photos.map((url, i) => (
              <div key={url} className="relative">
                <img src={url} alt="" className="w-20 h-20 rounded-lg object-cover border border-border" />
                <button className="absolute -top-2 -right-2 bg-red text-white rounded-full w-5 h-5 text-xs" title="Remove"
                  onClick={() => set('photos', form.photos.filter((_, j) => j !== i))}>×</button>
              </div>
            ))}
            {form.photos.length < 6 && (
              <label className="w-20 h-20 rounded-lg border border-dashed border-border flex items-center justify-center text-xs text-text-muted cursor-pointer hover:border-cyan">
                {uploading ? <Spinner size="sm" /> : '+ Photo'}
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={upload} disabled={uploading} />
              </label>
            )}
          </div>
        </div>

        <div className="border border-border rounded-xl p-3 space-y-3">
          <div className="text-xs font-semibold text-text-secondary">Details shown to buyers (all optional — use what fits)</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input label="Brand" value={form.details.brand} onChange={e => setIn('details', 'brand', e.target.value)} placeholder="Electricals, packaged goods" />
            <Select label="Condition" value={form.details.condition} onChange={e => setIn('details', 'condition', e.target.value)}>
              {CONDITION_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
            <Input label="Warranty" value={form.details.warranty} onChange={e => setIn('details', 'warranty', e.target.value)} placeholder="e.g. 12 months" />
            <Input label="Origin" value={form.details.origin} onChange={e => setIn('details', 'origin', e.target.value)} placeholder="e.g. Lake Victoria, Kasese" />
            <label className="text-xs flex items-center gap-2 text-text-secondary">
              <input type="checkbox" checked={form.details.perishable} onChange={e => setIn('details', 'perishable', e.target.checked)} /> Perishable (fresh fish, produce…)
            </label>
            <Input label="Available until" type="date" value={form.details.availableUntil} onChange={e => setIn('details', 'availableUntil', e.target.value)}
              note="Hidden from the shop after this date. Blank = no end." />
          </div>
        </div>

        <div className="border border-amber/40 rounded-xl p-3 space-y-3">
          <div className="text-xs font-semibold text-amber">🔒 Supplier — private, buyers never see this</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input label="Supplier name" value={form.supplier.name} onChange={e => setIn('supplier', 'name', e.target.value)} />
            <Input label="Location" value={form.supplier.location} onChange={e => setIn('supplier', 'location', e.target.value)} placeholder="Where the goods are" />
            <Input label="Phone" value={form.supplier.phone} onChange={e => setIn('supplier', 'phone', e.target.value)} placeholder="07XXXXXXXX" />
            <Input label="WhatsApp (with country code)" value={form.supplier.whatsapp} onChange={e => setIn('supplier', 'whatsapp', e.target.value)} placeholder="2567XXXXXXXX" />
          </div>
          <Input label="Notes" value={form.supplier.notes} onChange={e => setIn('supplier', 'notes', e.target.value)} placeholder="Quality, pickup, payment terms agreed…" />
          {(form.supplier.phone || form.supplier.whatsapp) && (
            <div className="flex gap-2">
              {form.supplier.phone && <a href={`tel:${form.supplier.phone.replace(/\s/g, '')}`}><Button size="xs" variant="secondary">📞 Call</Button></a>}
              {form.supplier.whatsapp && <a href={waLink(form.supplier.whatsapp, `Hello ${form.supplier.name || ''}, this is DwiSend about ${form.name}. `)} target="_blank" rel="noopener noreferrer"><Button size="xs" variant="success">WhatsApp supplier</Button></a>}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-end justify-between gap-3 flex-wrap mb-1">
            <div className="text-xs font-semibold text-text-secondary">Sizes, cost & price</div>
            <div className="w-44">
              <Input label="Margin % for this product" type="number" min="0" step="0.1" value={form.marginPct}
                onChange={e => set('marginPct', e.target.value)} placeholder={margin.pct != null && form.marginPct === '' ? `${margin.pct} (inherited)` : 'blank = inherit'} />
            </div>
          </div>
          <p className="text-[11px] text-text-muted mb-2">
            Cost = what you pay the supplier (private). Tick <b>Auto</b> to price at cost + margin
            {margin.pct != null ? <> — now <b>{margin.pct}%</b> from {margin.from}, rounded up to {ugx(round)}</> : <> — <span className="text-amber">no margin set yet</span></>}.
            Auto prices update when you change the margin.
            {onRequest ? ' Price on request: prices are optional and never shown to buyers.' : ' A size with no price is not sold.'}
          </p>
          <div className="space-y-2">
            {form.variants.map((v, i) => {
              const u = unitOf(v.unitId);
              const cost = Number(v.costUGX);
              const preview = v.priceAuto && v.costUGX !== '' && cost > 0 && margin.pct != null ? autoPrice(cost, margin.pct, round) : null;
              const price = v.priceAuto ? preview : (String(v.priceUGX).trim() === '' ? null : Number(v.priceUGX));
              const gain = price != null && v.costUGX !== '' ? price - cost : null;
              return (
                <div key={v._id || v.unitId} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-2 text-sm text-text-primary pb-3">{u?.name || '?'}</div>
                  <div className="col-span-3"><Input label={i === 0 ? 'Cost (UGX)' : undefined} type="number" min="0" step="1" value={v.costUGX} onChange={e => setVariant(i, 'costUGX', e.target.value)} /></div>
                  <label className="col-span-1 text-[11px] flex flex-col items-center gap-1 pb-3" title="Price = cost + margin">
                    {i === 0 && <span className="text-text-muted">Auto</span>}
                    <input type="checkbox" checked={v.priceAuto} onChange={e => setVariant(i, 'priceAuto', e.target.checked)} />
                  </label>
                  <div className="col-span-3">
                    {v.priceAuto
                      ? <div className="pb-3 text-sm text-text-primary">{i === 0 && <div className="text-xs font-bold text-text-muted uppercase tracking-wider mb-1.5">Price</div>}{preview != null ? ugx(preview) : '—'}</div>
                      : <Input label={i === 0 ? 'Price (UGX)' : undefined} type="number" min="1" step="1" value={v.priceUGX} onChange={e => setVariant(i, 'priceUGX', e.target.value)} />}
                  </div>
                  <div className="col-span-1"><Input label={i === 0 ? 'Min' : undefined} type="number" min="1" step="1" value={v.minOrderQty} onChange={e => setVariant(i, 'minOrderQty', e.target.value)} /></div>
                  <div className="col-span-2 pb-3 flex items-center gap-2 text-xs">
                    <input type="checkbox" title="On sale" checked={v.isActive !== false} onChange={e => setVariant(i, 'isActive', e.target.checked)} />
                    <button className="text-text-muted hover:text-red" title="Remove size" onClick={() => set('variants', form.variants.filter((_, j) => j !== i))}>×</button>
                    {gain != null && <span className={gain < 0 ? 'text-red' : 'text-green'}>{gain >= 0 ? '+' : ''}{gain.toLocaleString('en-US')}</span>}
                  </div>
                </div>
              );
            })}
          </div>
          {form.variants.length === 0 && <p className="text-xs text-text-muted">Add the sizes you sell this in:</p>}
          {addableUnits.length > 0 && (
            <div className="flex gap-2 mt-2 flex-wrap">
              {addableUnits.map(u => (
                <Button key={u._id} size="xs" variant="ghost" onClick={() => set('variants', [...form.variants, { unitId: u._id, priceUGX: '', costUGX: '', priceAuto: false, minOrderQty: 1, isActive: true }])}>+ {u.name}</Button>
              ))}
            </div>
          )}
        </div>

        {isNew && (
          <Input label={`Starting stock${baseUnit ? ` (${baseUnit})` : ''}`} type="number" min="0" step="1" value={form.stockQty}
            note={`Total available${baseUnit ? `, in ${baseUnit}` : ''}. Later changes: use the Stock button.`} onChange={e => set('stockQty', e.target.value)} />
        )}

        <div className="flex gap-2 pt-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1" loading={saving} disabled={uploading} onClick={save}>{isNew ? 'Add product' : 'Save'}</Button>
        </div>
      </div>
    </Modal>
  );
}

function StockModal({ product, onClose, onSaved }) {
  const [mode, setMode] = useState('add');
  const [qty, setQty]   = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const n = Number(qty);
  const after = mode === 'set' ? n : product.stockQty + n;

  const save = async () => {
    setSaving(true);
    try {
      await marketAPI.changeStock(product._id, { mode, qty: n, note });
      toast.success('Stock updated');
      onSaved();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Stock — ${product.name}`} size="sm">
      <div className="space-y-3">
        <div className="text-sm">In store now: <span className="text-text-primary font-bold">{product.stockQty.toLocaleString()} {product.baseUnit}</span></div>
        <Select label="What happened?" value={mode} onChange={e => setMode(e.target.value)}>
          <option value="add">Stock in / out (delivery +, loss −)</option>
          <option value="set">Counted the store — set exact amount</option>
        </Select>
        <Input label={mode === 'set' ? `Exact amount (${product.baseUnit})` : `Change (${product.baseUnit}) — use minus for losses`} type="number" step="1"
          value={qty} onChange={e => setQty(e.target.value)} placeholder={mode === 'set' ? 'e.g. 950' : 'e.g. 500 or -20'} />
        <Input label="Reason" value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. delivery from supplier / weevil loss / stock count" />
        {qty !== '' && Number.isInteger(n) && (
          <div className={`text-xs ${after < 0 ? 'text-red' : 'text-text-muted'}`}>After: {after.toLocaleString()} {product.baseUnit}</div>
        )}
        <div className="flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1" loading={saving} onClick={save}>Save</Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Sizes / categories / delivery areas (one generic editor) ─────────────────
const CATALOG_UI = {
  unit: {
    title: 'Sizes', empty: 'No sizes', addLabel: '+ Add size',
    help: 'How products are sold. "Measured in" is how stock is counted (kg, piece, litre…); "Amount" is how much stock one of this size uses (50 kg bag = 50 kg, tray of 30 eggs = 30 piece). Once a size is used by a product its amount is locked — create a new size instead.',
    fields: [
      { key: 'name', label: 'Name', placeholder: 'e.g. 25 kg bag, tray (30), box of 10' },
      { key: 'baseQty', label: 'Amount', type: 'number', placeholder: '25' },
      { key: 'baseUnit', label: 'Measured in', placeholder: 'kg / piece / litre', default: 'kg' },
      { key: 'sortOrder', label: 'Order', type: 'number', default: 0 },
    ],
    show: u => `${u.baseQty} ${u.baseUnit}`,
  },
  category: {
    title: 'Categories', empty: 'No categories', addLabel: '+ Add category',
    help: 'Hiding a category hides all its products from the shop. Margin % (private) is used for auto prices in this category — blank = the default margin in Settings.',
    fields: [
      { key: 'name', label: 'Name', placeholder: 'e.g. Fertilizers' },
      { key: 'marginPct', label: 'Margin % (private, blank = default)', type: 'number', optional: true, step: '0.1' },
      { key: 'sortOrder', label: 'Order', type: 'number', default: 0 },
    ],
    show: c => (c.marginPct != null ? `margin ${c.marginPct}%` : 'default margin'),
  },
  zone: {
    title: 'Delivery Areas', empty: 'No delivery areas yet', addLabel: '+ Add area',
    help: 'Buyers choose one at checkout and its fee is added to the order. With no areas set up, delivery is free and arranged by phone.',
    fields: [
      { key: 'name', label: 'Area', placeholder: 'e.g. Kampala Central' },
      { key: 'feeUGX', label: 'Delivery fee (UGX)', type: 'number', placeholder: '15000' },
      { key: 'notes', label: 'Note for buyers', placeholder: 'e.g. Tue & Fri deliveries' },
      { key: 'sortOrder', label: 'Order', type: 'number', default: 0 },
    ],
    show: z => `${ugx(z.feeUGX)}${z.notes ? ` · ${z.notes}` : ''}`,
  },
};

function CatalogSection({ kind }) {
  const cfg = CATALOG_UI[kind];
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await marketAPI.listCatalog(kind)).items || []); }
    catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [kind]);
  useEffect(() => { load(); }, [load]);

  const toggle = async it => {
    try { await marketAPI.updateCatalog(kind, it._id, { isActive: !it.isActive }); load(); }
    catch (err) { toast.error(err.message); }
  };

  return (
    <Card>
      <SectionHeader title={cfg.title} action={<Button size="sm" onClick={() => setEditing({})}>{cfg.addLabel}</Button>} />
      <p className="text-xs text-text-muted mb-3">{cfg.help}</p>
      {loading ? <div className="py-10 flex justify-center"><Spinner /></div>
        : items.length === 0 ? <Empty title={cfg.empty} />
        : (
          <Table headers={['Name', 'Details', 'Status', '']}>
            {items.map(it => (
              <tr key={it._id} className={it.isActive ? '' : 'opacity-50'}>
                <Td className="text-text-primary font-semibold">{it.name}</Td>
                <Td>{cfg.show(it)}</Td>
                <Td><Badge label={it.isActive ? 'Active' : 'Hidden'} color={it.isActive ? 'green' : 'gray'} /></Td>
                <Td>
                  <div className="flex gap-1">
                    <Button size="xs" variant="secondary" onClick={() => setEditing(it)}>Edit</Button>
                    <Button size="xs" variant="ghost" onClick={() => toggle(it)}>{it.isActive ? 'Hide' : 'Show'}</Button>
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      {editing && <CatalogModal kind={kind} item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </Card>
  );
}

function CatalogModal({ kind, item, onClose, onSaved }) {
  const cfg = CATALOG_UI[kind];
  const isNew = !item._id;
  const [form, setForm] = useState(() => Object.fromEntries(cfg.fields.map(f => [f.key, item[f.key] ?? f.default ?? ''])));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const body = Object.fromEntries(cfg.fields.map(f => {
      const raw = form[f.key] ?? '';
      if (f.type !== 'number') return [f.key, String(raw).trim()];
      return [f.key, f.optional && String(raw).trim() === '' ? null : Number(raw)];
    }));
    setSaving(true);
    try {
      if (isNew) await marketAPI.createCatalog(kind, body); else await marketAPI.updateCatalog(kind, item._id, body);
      toast.success('Saved');
      onSaved();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={`${isNew ? 'Add' : 'Edit'} — ${cfg.title}`} size="sm">
      <div className="space-y-3">
        {cfg.fields.map(f => (
          <Input key={f.key} label={f.label} type={f.type || 'text'} step={f.type === 'number' ? (f.step || '1') : undefined}
            placeholder={f.placeholder} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} />
        ))}
        <div className="flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1" loading={saving} onClick={save}>Save</Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Settings ─────────────────────────────────────────────────────────────────
function SettingsSection() {
  const [s, setS]           = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await marketAPI.getSettings();
      setS({ ...r.settings, sellerFeePercent: r.settings.sellerFeePercent ?? '' });
    } catch (err) { toast.error(err.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!s) return <Card><div className="py-10 flex justify-center"><Spinner /></div></Card>;
  const set = (k, v) => setS(p => ({ ...p, [k]: v }));

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await marketAPI.saveSettings(patch || {
        sellerMerchantId: s.sellerMerchantId, alertPhone: s.alertPhone,
        contactPhone: s.contactPhone, contactWhatsapp: s.contactWhatsapp,
        paymentExpiryMinutes: Math.round(Number(s.paymentExpiryHours ?? s.paymentExpiryMinutes / 60) * 60),
        minOrderUGX: Number(s.minOrderUGX || 0),
        defaultMarginPct: s.defaultMarginPct === '' || s.defaultMarginPct == null ? null : Number(s.defaultMarginPct),
        priceRoundUGX: Number(s.priceRoundUGX || 1),
        chatRefreshSeconds: Number(s.chatRefreshSeconds || 5),
        maxUnpaidOrders: Number(s.maxUnpaidOrders || 3),
        sellerFeePercent: s.sellerFeePercent === '' ? undefined : Number(s.sellerFeePercent),
      });
      setS({ ...r.settings, sellerFeePercent: r.settings.sellerFeePercent ?? '' });
      toast.success(r.settings.repriced ? `Settings saved — ${r.settings.repriced} product(s) re-priced` : 'Settings saved');
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const hours = s.paymentExpiryHours ?? s.paymentExpiryMinutes / 60;

  return (
    <div className="space-y-4">
      <Card>
        <SectionHeader title="Shop open to buyers" action={
          <Button size="sm" variant={s.enabled ? 'danger' : 'success'} loading={saving}
            onClick={() => save({ enabled: !s.enabled })}>{s.enabled ? 'Close shop' : 'Open shop'}</Button>
        } />
        <p className="text-sm">{s.enabled
          ? <span className="text-green font-semibold">OPEN — buyers can see products and order.</span>
          : <span className="text-amber font-semibold">CLOSED — only admins see the shop (use this to test).</span>}</p>
      </Card>

      <Card>
        <SectionHeader title="Marketing page — dwifintech.com/dwisend" action={
          <Button size="sm" variant={s.landingEnabled ? 'danger' : 'success'} loading={saving}
            onClick={() => save({ landingEnabled: !s.landingEnabled })}>{s.landingEnabled ? 'Turn page off' : 'Turn page on'}</Button>
        } />
        <p className="text-sm">{s.landingEnabled
          ? <span className="text-green font-semibold">ON — anyone with the link can see it. Visits and clicks are being counted.</span>
          : <span className="text-amber font-semibold">OFF — only admins see a preview; others go to the login page. Nothing is counted.</span>}</p>
        <p className="text-xs text-text-muted mt-2">
          <a href="/dwisend" target="_blank" rel="noopener noreferrer" className="text-cyan font-semibold">Open the page ↗</a>
          {' '}· It follows the shop switch: closed = "opening soon, sign up", open = "shop now".
          For ads, add a source so you can compare them: <span className="font-mono">dwifintech.com/dwisend?src=facebook</span>
        </p>
        <LandingStats />
      </Card>

      <Card>
        <SectionHeader title="Seller sign-up — Sell to DWI" action={
          <Button size="sm" variant={s.sellersEnabled ? 'danger' : 'success'} loading={saving}
            onClick={() => save({ sellersEnabled: !s.sellersEnabled })}>{s.sellersEnabled ? 'Close sign-up' : 'Open sign-up'}</Button>
        } />
        <p className="text-sm">{s.sellersEnabled
          ? <span className="text-green font-semibold">OPEN — any logged-in user can apply and list goods (DwiSend → Sell to DWI).</span>
          : <span className="text-amber font-semibold">CLOSED — only admins see the seller screens.</span>}</p>
        <p className="text-xs text-text-muted mt-2">Independent of the shop: you can open seller sign-up first to build supply. You vet every seller (Sellers tab) and listing (Supply tab).</p>
      </Card>

      <Card>
        <SectionHeader title="Buy requests + quotes" action={
          <Button size="sm" variant={s.requestsEnabled ? 'danger' : 'success'} loading={saving}
            onClick={() => save({ requestsEnabled: !s.requestsEnabled })}>{s.requestsEnabled ? 'Close requests' : 'Open requests'}</Button>
        } />
        <p className="text-sm">{s.requestsEnabled
          ? <span className="text-green font-semibold">OPEN — logged-in buyers can ask DWI for a price (DwiSend → Buy requests) and pay accepted quotes.</span>
          : <span className="text-amber font-semibold">CLOSED — only admins see the buy-request screens.</span>}</p>
        <p className="text-xs text-text-muted mt-2">Independent of the shop: quotes accepted while the shop is closed are still normal orders, paid by link. You answer requests in the Demand tab.</p>
      </Card>

      <Card>
        <SectionHeader title="Pricing (private)" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Default margin %" type="number" min="0" step="0.1" value={s.defaultMarginPct ?? ''} placeholder="e.g. 15"
            note="Added to the supplier cost for auto-priced sizes. A category or product margin overrides it. Blank = not set." onChange={e => set('defaultMarginPct', e.target.value)} />
          <Input label="Round auto prices up to (UGX)" type="number" min="1" step="1" value={s.priceRoundUGX}
            note="e.g. 100 → 3,360 becomes 3,400; 500 → 3,500." onChange={e => set('priceRoundUGX', e.target.value)} />
        </div>
        <p className="text-[11px] text-text-muted mt-2">Changing these re-prices every auto-priced size straight away. Fixed (typed) prices never change on their own.</p>
        <div className="mt-4"><Button loading={saving} onClick={() => save()}>Save settings</Button></div>
      </Card>

      <Card>
        <SectionHeader title="Selling & alerts" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Select label="Seller account (money goes to this merchant wallet)" value={s.sellerMerchantId} onChange={e => set('sellerMerchantId', e.target.value)}>
            <option value="">— choose —</option>
            {s.merchants.map(m => <option key={m._id} value={m._id}>{m.businessName} ({m.code})</option>)}
          </Select>
          <Input label="Seller payment fee %" type="number" step="0.01" min="0" value={s.sellerFeePercent}
            note="Fee kept from each payment to the seller wallet. Blank = standard merchant fee tables. 0 = no fee."
            onChange={e => set('sellerFeePercent', e.target.value)} />
          <Input label="SMS alerts to (your phone)" value={s.alertPhone} placeholder="+2567XXXXXXXX"
            note="New orders, payments, refund warnings and new messages." onChange={e => set('alertPhone', e.target.value)} />
          <Input label="Contact phone shown to buyers" value={s.contactPhone} placeholder="+2567XXXXXXXX"
            note="Shown in the shop as DwiSend's number. Blank = hidden." onChange={e => set('contactPhone', e.target.value)} />
          <Input label="WhatsApp shown to buyers" value={s.contactWhatsapp} placeholder="2567XXXXXXXX"
            note="With country code. Opens a WhatsApp chat with DwiSend. Blank = hidden." onChange={e => set('contactWhatsapp', e.target.value)} />
          <Input label="Time to pay an order (hours)" type="number" min="1" step="1" value={hours}
            note="Unpaid orders close after this and stock returns to the shelf." onChange={e => set('paymentExpiryHours', e.target.value)} />
          <Input label="Minimum order (UGX)" type="number" min="0" step="1" value={s.minOrderUGX}
            note="0 = no minimum. Delivery fee not counted." onChange={e => set('minOrderUGX', e.target.value)} />
          <Input label="Unpaid orders per buyer" type="number" min="1" max="50" step="1" value={s.maxUnpaidOrders}
            note="Unpaid orders hold stock until they expire. A buyer can't place more than this until they pay or cancel one." onChange={e => set('maxUnpaidOrders', e.target.value)} />
          <Input label="Chat refresh (seconds)" type="number" min="3" max="60" step="1" value={s.chatRefreshSeconds}
            note="How often an open conversation checks for new messages (3–60). Lower feels quicker; 5 is a good default." onChange={e => set('chatRefreshSeconds', e.target.value)} />
        </div>
        <div className="mt-4"><Button loading={saving} onClick={() => save()}>Save settings</Button></div>
      </Card>

      <Card>
        <SectionHeader title="Managed elsewhere" />
        <ul className="text-xs text-text-muted space-y-1 list-disc pl-4">
          <li>Mobile-money collection costs (Yo! fee) — Admin → Fee Rules / system config.</li>
          <li>Withdrawing sales money — Merchant page → Settlements, as the seller account.</li>
          <li>Outside sellers: they sell TO DWI (Sellers + Supply tabs); DWI is the seller of record to buyers. You pay sellers outside the app and record it in Payables.</li>
        </ul>
      </Card>
    </div>
  );
}
